import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanePointEditor } from './useLanePointEditor';
import { useSpeedPointPlacer } from './useSpeedPointPlacer';
import { catmullRomSpline, SPLINE_SAMPLES_EDIT } from './splineUtils';
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  useGoogleMap,
} from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../../../API/api';
import { fetchGoogleMapsApiKey } from '../../../../services/googleMapsKeyService';
import terrainImg from '../../../../assets/terrain.jpg';
import satelliteImg from '../../../../assets/satellite.jpg';
import { useIsdBlockOverlays } from './useIsdBlockOverlays';
import { useIsdBlockDrawing } from './useIsdBlockDrawing';
import { useIsdOppositePointPick } from './useIsdOppositePointPick';
import { useIsdOppositeBlockOverlays } from './useIsdOppositeBlockOverlays';
import OrthoMapsInfoModal from '../InternalComponents/OrthoMapsInfoModal';
import './PathSelectorMap.css';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // Geographic center of USA (Lebanon, Kansas)
const DEFAULT_ZOOM = 20;
const LOG = (tag, ...args) => console.log(`[GMaps:${tag}]`, ...args);

function toLatLng(center) {
  if (!center) return DEFAULT_CENTER;
  if (Array.isArray(center)) return { lat: center[0], lng: center[1] };
  return center;
}

const METERS_TO_FEET = 3.28084;

/** Haversine distance between two points in meters */
function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getPolylineDistanceMeters(points = []) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) continue;
    total += haversineMeters(a.lat, a.lng, b.lat, b.lng);
  }
  return total;
}

/** Normalize path point to { lat, lng } */
function pathPointToLatLng(p) {
  if (!p) return null;
  if (Array.isArray(p)) return { lat: p[1], lng: p[0] };
  const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
  const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
  return { lat, lng };
}

/**
 * Resample a path to coordinates every N feet along the path.
 * @param path - Array of points: { lat, lng } or [lng, lat]
 * @param intervalFeet - Spacing in feet (e.g. 10)
 * @returns Array of { lat, lng } for each point every intervalFeet
 */
export function getPathCoordinatesEveryFeet(path, intervalFeet = 10) {
  const pts = path.map(pathPointToLatLng).filter((p) => p != null && p.lat != null && p.lng != null);
  if (pts.length < 2) return pts.length ? [pts[0]] : [];
  const result = [];
  const intervalMeters = intervalFeet / METERS_TO_FEET;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const segLenM = haversineMeters(a.lat, a.lng, b.lat, b.lng);
    const segLenFt = segLenM * METERS_TO_FEET;
    result.push({ lat: a.lat, lng: a.lng });
    let dFt = intervalFeet;
    while (dFt < segLenFt - 1e-6) {
      const t = dFt / segLenFt;
      result.push({
        lat: a.lat + t * (b.lat - a.lat),
        lng: a.lng + t * (b.lng - a.lng),
      });
      dFt += intervalFeet;
    }
  }
  result.push(pts[pts.length - 1]);
  return result;
}

const mapContainerStyle = { width: '100%', height: '100%' };

/**
 * PathSelectorMap - Interactive map for selecting road paths.
 * Uses Google Maps API with Directions Service for road-snapped routing.
 */
/**
 * Optional image overlay:
 * - { url: string, quad: [{lat,lng} x4] } where corners are [top-left, top-right, bottom-right, bottom-left]
 * - or legacy { url: string, bounds: { south, west, north, east } }.
 * PNG (or any image URL) will be drawn in map coordinates.
 */
const PathSelectorMap = ({
  pathsPanelMode = 'path-creator',
  onPathsPanelModeChange,
  routeStageHint = null,
  waypointsLocked = false,
  waypoints = [],
  setWaypoints,
  onPathChange,
  waypointColor = '#DD4130',
  fullPathStrokeColor = '#DD4130',
  fullPathPathData = null,
  fitToPathData = null,
  onFitToPathComplete,
  fitToBounds = null,
  onFitToBoundsComplete,
  segmentVisibility = [],
  activeSegmentIndex = 0,
  activeEntityId = null,
  highlightPathData = null,
  highlightColor = '#f59e0b',
  activeSegmentPathData = null,
  freezeActiveSegmentPath = false,
  persistentPathOverlays = [],
  allowAddWaypointOnClick = true,
  manualSplineMode = false,
  centerOnWaypointClick = false,
  initialCenter,
  initialZoom = DEFAULT_ZOOM,
  isVisible = true,
  imageOverlays = [],
  lasBoundingBoxes = [],
  selectedLasBoundingBoxIndex = -1,
  overlayOpacity = 0.9,
  overlayBrightness = 1,
  overlayContrast = 1,
  onImageOverlaysLoadProgress,
  orthoFetchingMeta = false,
  orthoTilesLoaded = 0,
  orthoTilesTotal = 0,
  intersectionDrawingMode = null,
  onIntersectionDrawingModeChange,
  onIntersectionOverlayComplete,
  onIntersectionOverlayUpdate,
  onIntersectionOverlayRemove = () => {},
  intersectionOverlays = [],
  intersections = [],
  activeIntersectionId = null,
  selectedTargetId = null,
  onAddIntersection,
  onUpdateIntersection,
  onSetActiveIntersection,
  pendingIsdDraft = null,
  onCancelPendingIsdDraft,
  oppositePointPickActive = false,
  onOppositeBlockPicked,
  onCancelOppositePointPick,
  oppositeBlockDraft = null,
  onUpdateOppositeBlockDraft,
  // Auto-trace: when active, the next map click is captured as the trace seed
  // (a point on the lane marking) instead of adding a waypoint.
  traceSeedPickActive = false,
  onTraceSeedPicked,
  curvePoints = [],
  onCurvedLinePointsChange,
  measurePoints = [],
  onMeasurePointsChange,
  curvedLineEditPointCount = 5,
  // Lane point editor props
  laneEditMode = false,
  laneEditWaypoints = [],
  laneEditSelectedIndices = [],
  laneEditMarkerColor = '#2563eb',
  onLaneEditSelectionChange,
  onLaneEditWaypointsMoved,
  onLaneEditWaypointInserted,
  onLaneEditWaypointRemoved,
  // Lane edit toolbar props
  laneEditCanUndo = false,
  laneEditCanRedo = false,
  onLaneEditUndo,
  onLaneEditRedo,
  onLaneEditDeleteSelected,
  onLaneEditSelectAll,
  onLaneEditClearSelection,
  // Speed edit mode props
  speedEditMode = false,
  speedEditWaypoints = [],
  speedEditSegments = [],
  onSpeedBreakpointAdded,
}) => {
  const [pathGeoJson, setPathGeoJson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [directionsResult, setDirectionsResult] = useState(null);
  const [apiKey, setApiKey] = useState(() => GOOGLE_MAPS_API_KEY || null);
  const [keyError, setKeyError] = useState(null);
  const onPathChangeRef = useRef(onPathChange);
  const setWaypointsRef = useRef(setWaypoints);

  useEffect(() => {
    onPathChangeRef.current = onPathChange;
  }, [onPathChange]);

  useEffect(() => {
    setWaypointsRef.current = setWaypoints;
  }, [setWaypoints]);

  // Fetch API key from backend (Secrets Manager) when env key is missing
  useEffect(() => {
    LOG('PathSelectorMap', 'mount/effect', { isVisible, hasEnvKey: !!GOOGLE_MAPS_API_KEY });
    if (GOOGLE_MAPS_API_KEY) {
      setApiKey(GOOGLE_MAPS_API_KEY);
      return;
    }
    if (!isVisible) return; // Defer fetch until Paths tab is shown
    let cancelled = false;
    setKeyError(null);
    fetchGoogleMapsApiKey()
      .then((key) => {
        if (!cancelled) {
          setApiKey(key || '');
          if (!key) setKeyError('Could not load Google Maps API key from server.');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setApiKey('');
          setKeyError(err?.message || 'Failed to fetch Google Maps API key.');
        }
      });
    return () => { cancelled = true; };
  }, [isVisible]);

  const handlePathChange = useCallback((geojson) => {
    setPathGeoJson(geojson);
    onPathChangeRef.current?.(geojson);
  }, []);

  const handleSetWaypoints = useCallback((nextWaypointsOrUpdater) => {
    setWaypointsRef.current?.(nextWaypointsOrUpdater);
  }, []);

  const handleClearPath = useCallback(() => {
    handleSetWaypoints([]);
    setPathGeoJson(null);
    setLoading(false);
    setDirectionsResult(null);
    onPathChangeRef.current?.(null);
  }, [handleSetWaypoints]);

  // Only render map when we have a stable non-empty key - useJsApiLoader must not be called with changing/empty apiKey
  const hasValidKey = Boolean(apiKey && typeof apiKey === 'string' && apiKey.trim());
  if (!hasValidKey) {
    return (
      <div className="path-selector-map-container">
        <div className="path-selector-map-wrapper">
          {apiKey === null && !keyError && (
            <div className="path-map-loading">Loading map…</div>
          )}
          {keyError && (
            <div className="path-map-error">{keyError}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <PathSelectorMapWithKey
      apiKey={apiKey.trim()}
      pathsPanelMode={pathsPanelMode}
      onPathsPanelModeChange={onPathsPanelModeChange}
      routeStageHint={routeStageHint}
      waypointsLocked={waypointsLocked}
      waypoints={waypoints}
      setWaypoints={handleSetWaypoints}
      onPathChange={handlePathChange}
      handleClearPath={handleClearPath}
      waypointColor={waypointColor}
      fullPathStrokeColor={fullPathStrokeColor}
      fullPathPathData={fullPathPathData}
      fitToPathData={fitToPathData}
      onFitToPathComplete={onFitToPathComplete}
      segmentVisibility={segmentVisibility}
      activeSegmentIndex={activeSegmentIndex}
      activeEntityId={activeEntityId}
      highlightPathData={highlightPathData}
      highlightColor={highlightColor}
      activeSegmentPathData={activeSegmentPathData}
      freezeActiveSegmentPath={freezeActiveSegmentPath}
      persistentPathOverlays={persistentPathOverlays}
      allowAddWaypointOnClick={allowAddWaypointOnClick}
      manualSplineMode={manualSplineMode}
      centerOnWaypointClick={centerOnWaypointClick}
      initialCenter={initialCenter}
      initialZoom={initialZoom}
      isVisible={isVisible}
      pathGeoJson={pathGeoJson}
      loading={loading}
      directionsResult={directionsResult}
      setDirectionsResult={setDirectionsResult}
      setLoading={setLoading}
      imageOverlays={imageOverlays}
      lasBoundingBoxes={lasBoundingBoxes}
      selectedLasBoundingBoxIndex={selectedLasBoundingBoxIndex}
      fitToBounds={fitToBounds}
      onFitToBoundsComplete={onFitToBoundsComplete}
      overlayOpacity={overlayOpacity}
      overlayBrightness={overlayBrightness}
      overlayContrast={overlayContrast}
      onImageOverlaysLoadProgress={onImageOverlaysLoadProgress}
      orthoFetchingMeta={orthoFetchingMeta}
      orthoTilesLoaded={orthoTilesLoaded}
      orthoTilesTotal={orthoTilesTotal}
      intersectionDrawingMode={intersectionDrawingMode}
      onIntersectionDrawingModeChange={onIntersectionDrawingModeChange}
      onIntersectionOverlayComplete={onIntersectionOverlayComplete}
      onIntersectionOverlayUpdate={onIntersectionOverlayUpdate}
      onIntersectionOverlayRemove={onIntersectionOverlayRemove}
      intersectionOverlays={intersectionOverlays}
      intersections={intersections}
      activeIntersectionId={activeIntersectionId}
      selectedTargetId={selectedTargetId}
      onAddIntersection={onAddIntersection}
      onUpdateIntersection={onUpdateIntersection}
      onSetActiveIntersection={onSetActiveIntersection}
      pendingIsdDraft={pendingIsdDraft}
      onCancelPendingIsdDraft={onCancelPendingIsdDraft}
      oppositePointPickActive={oppositePointPickActive}
      onOppositeBlockPicked={onOppositeBlockPicked}
      onCancelOppositePointPick={onCancelOppositePointPick}
      oppositeBlockDraft={oppositeBlockDraft}
      onUpdateOppositeBlockDraft={onUpdateOppositeBlockDraft}
      traceSeedPickActive={traceSeedPickActive}
      onTraceSeedPicked={onTraceSeedPicked}
      curvePoints={curvePoints}
      onCurvedLinePointsChange={onCurvedLinePointsChange}
      measurePoints={measurePoints}
      onMeasurePointsChange={onMeasurePointsChange}
      curvedLineEditPointCount={curvedLineEditPointCount}
      laneEditMode={laneEditMode}
      laneEditWaypoints={laneEditWaypoints}
      laneEditSelectedIndices={laneEditSelectedIndices}
      laneEditMarkerColor={laneEditMarkerColor}
      onLaneEditSelectionChange={onLaneEditSelectionChange}
      onLaneEditWaypointsMoved={onLaneEditWaypointsMoved}
      onLaneEditWaypointInserted={onLaneEditWaypointInserted}
      onLaneEditWaypointRemoved={onLaneEditWaypointRemoved}
      laneEditCanUndo={laneEditCanUndo}
      laneEditCanRedo={laneEditCanRedo}
      onLaneEditUndo={onLaneEditUndo}
      onLaneEditRedo={onLaneEditRedo}
      onLaneEditDeleteSelected={onLaneEditDeleteSelected}
      onLaneEditSelectAll={onLaneEditSelectAll}
      onLaneEditClearSelection={onLaneEditClearSelection}
      speedEditMode={speedEditMode}
      speedEditWaypoints={speedEditWaypoints}
      speedEditSegments={speedEditSegments}
      onSpeedBreakpointAdded={onSpeedBreakpointAdded}
    />
  );
};

/**
 * PathSelectorMapWithKey - Renders the map with a stable API key.
 * Mounted only when apiKey is available, so useJsApiLoader never sees a key change.
 */
function PathSelectorMapWithKey({
  apiKey,
  waypoints,
  setWaypoints,
  onPathChange,
  handleClearPath,
  waypointColor = '#DD4130',
  fullPathStrokeColor = '#DD4130',
  fullPathPathData = null,
  fitToPathData = null,
  onFitToPathComplete,
  segmentVisibility = [],
  activeSegmentIndex = 0,
  activeEntityId = null,
  highlightPathData = null,
  highlightColor = '#f59e0b',
  activeSegmentPathData = null,
  freezeActiveSegmentPath = false,
  persistentPathOverlays = [],
  allowAddWaypointOnClick = true,
  manualSplineMode = false,
  centerOnWaypointClick = false,
  initialCenter,
  initialZoom,
  isVisible,
  pathGeoJson,
  loading,
  directionsResult,
  setDirectionsResult,
  setLoading,
  imageOverlays = [],
  lasBoundingBoxes = [],
  selectedLasBoundingBoxIndex = -1,
  fitToBounds = null,
  onFitToBoundsComplete,
  overlayOpacity = 0.9,
  overlayBrightness = 1,
  overlayContrast = 1,
  onImageOverlaysLoadProgress,
  orthoFetchingMeta = false,
  orthoTilesLoaded = 0,
  orthoTilesTotal = 0,
  pathsPanelMode = 'path-creator',
  onPathsPanelModeChange,
  routeStageHint = null,
  waypointsLocked = false,
  intersectionDrawingMode = null,
  onIntersectionDrawingModeChange,
  onIntersectionOverlayComplete,
  onIntersectionOverlayUpdate,
  onIntersectionOverlayRemove = () => {},
  intersectionOverlays = [],
  intersections = [],
  activeIntersectionId = null,
  selectedTargetId = null,
  onAddIntersection,
  onUpdateIntersection,
  onSetActiveIntersection,
  pendingIsdDraft = null,
  onCancelPendingIsdDraft,
  oppositePointPickActive = false,
  onOppositeBlockPicked,
  onCancelOppositePointPick,
  oppositeBlockDraft = null,
  onUpdateOppositeBlockDraft,
  // Auto-trace: when active, the next map click is captured as the trace seed
  // (a point on the lane marking) instead of adding a waypoint.
  traceSeedPickActive = false,
  onTraceSeedPicked,
  curvePoints = [],
  onCurvedLinePointsChange,
  measurePoints = [],
  onMeasurePointsChange,
  curvedLineEditPointCount = 5,
  // Lane point editor props (rubber-band multi-select)
  laneEditMode = false,
  laneEditWaypoints = [],
  laneEditSelectedIndices = [],
  laneEditMarkerColor = '#2563eb',
  onLaneEditSelectionChange,
  onLaneEditWaypointsMoved,
  onLaneEditWaypointInserted,
  onLaneEditWaypointRemoved,
  // Lane edit toolbar props
  laneEditCanUndo = false,
  laneEditCanRedo = false,
  onLaneEditUndo,
  onLaneEditRedo,
  onLaneEditDeleteSelected,
  onLaneEditSelectAll,
  onLaneEditClearSelection,
  // Speed edit mode props
  speedEditMode = false,
  speedEditWaypoints = [],
  speedEditSegments = [],
  onSpeedBreakpointAdded,
}) {
  const mapRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const laneEditHintRef = useRef(null);
  const laneEditGuideRef = useRef(null);
  const isMapDraggingRef = useRef(false);
  const lastMapDragEndTsRef = useRef(0);
  const renderCountRef = useRef(0);
  const [showLaneEditGuide, setShowLaneEditGuide] = useState(false);
  const [showOrthoMapsInfo, setShowOrthoMapsInfo] = useState(false);
  // Ortho tiles that exhausted all retries, plus a nonce the in-bar "Retry"
  // button bumps to ask ImageOverlays to re-attempt just those tiles (no full
  // Drive re-list needed).
  const [orthoFailedCount, setOrthoFailedCount] = useState(0);
  const [orthoRetryNonce, setOrthoRetryNonce] = useState(0);
  // Manual spline mode undo/redo stack
  const manualRedoStackRef = useRef([]);
  // Stable center: set once on mount so the map is not recentered on every re-render (avoids jump-to-initial on click)
  const centerRef = useRef(toLatLng(initialCenter) || DEFAULT_CENTER);
  renderCountRef.current += 1;
  LOG('PathSelectorMapWithKey', 'render', {
    renderCount: renderCountRef.current,
    waypointsCount: waypoints.length,
    initialCenter,
    initialZoom,
    isVisible,
  });
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    preventGoogleFontsLoading: true,
    version: 'weekly',
    /* Do not add libraries: ['drawing'] here - it would change loader options and conflict with
       other useJsApiLoader usages. Drawing is loaded dynamically via importLibrary('drawing'). */
  });

  const enforceFlatMapView = useCallback((mapInstance) => {
    if (!mapInstance) return;
    // Prevent automatic tilt/3D camera in hybrid/satellite as users zoom in.
    mapInstance.setOptions({
      rotateControl: false,
      tilt: 0,
      heading: 0,
      tiltInteractionEnabled: false,
      headingInteractionEnabled: false,
    });
    if (typeof mapInstance.setTilt === 'function') mapInstance.setTilt(0);
    if (typeof mapInstance.setHeading === 'function') mapInstance.setHeading(0);
  }, []);

  const onMapLoad = useCallback((map) => {
    LOG('onMapLoad', 'map loaded', { center: map.getCenter()?.toString(), zoom: map.getZoom() });
    mapRef.current = map;

    const g = window.google?.maps;
    if (!g) return;
    enforceFlatMapView(map);

    const terrainBtn = document.createElement('button');
    terrainBtn.type = 'button';
    terrainBtn.className = 'path-terrain-btn';
    terrainBtn.title = 'Show map';
    terrainBtn.setAttribute('aria-label', 'Show map');
    const terrainImgEl = document.createElement('img');
    terrainImgEl.src = terrainImg;
    terrainImgEl.alt = 'Map';
    terrainBtn.appendChild(terrainImgEl);
    terrainBtn.addEventListener('click', () => {
      map.setMapTypeId('roadmap');
    });

    const satelliteBtn = document.createElement('button');
    satelliteBtn.type = 'button';
    satelliteBtn.className = 'path-terrain-btn path-satellite-btn';
    satelliteBtn.title = 'Toggle satellite view';
    satelliteBtn.setAttribute('aria-label', 'Show satellite');
    const satelliteImgEl = document.createElement('img');
    satelliteImgEl.src = satelliteImg;
    satelliteImgEl.alt = 'Satellite';
    satelliteBtn.appendChild(satelliteImgEl);
    satelliteBtn.addEventListener('click', () => {
      const cur = map.getMapTypeId();
      map.setMapTypeId(cur === 'satellite' || cur === 'hybrid' ? 'roadmap' : 'hybrid');
      enforceFlatMapView(map);
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'path-map-type-controls';
    wrapper.appendChild(terrainBtn);
    wrapper.appendChild(satelliteBtn);

    if (mapWrapperRef.current) {
      mapWrapperRef.current.appendChild(wrapper);
    }

    const wrapperEl = mapWrapperRef.current;
    g.event.addListener(map, 'dragstart', () => {
      isMapDraggingRef.current = true;
      if (wrapperEl) wrapperEl.classList.add('path-selector-map-dragging');
    });
    g.event.addListener(map, 'dragend', () => {
      isMapDraggingRef.current = false;
      lastMapDragEndTsRef.current = Date.now();
      if (wrapperEl) wrapperEl.classList.remove('path-selector-map-dragging');
    });
    g.event.addListener(map, 'zoom_changed', () => enforceFlatMapView(map));
    g.event.addListener(map, 'maptypeid_changed', () => enforceFlatMapView(map));
  }, [enforceFlatMapView]);

  const handleMapClick = useCallback(
    (e) => {
      // Drag on Google Maps can emit a trailing click; ignore it to avoid accidental waypoint inserts.
      if (isMapDraggingRef.current || (Date.now() - lastMapDragEndTsRef.current) < 180) return;
      // Auto-trace seed pick: capture this click as the trace start point and
      // do nothing else (don't add a waypoint).
      if (traceSeedPickActive) {
        const sLat = e.latLng?.lat();
        const sLng = e.latLng?.lng();
        if (sLat != null && sLng != null) onTraceSeedPicked?.({ lat: sLat, lng: sLng });
        return;
      }
      if (!allowAddWaypointOnClick) return;
      LOG('handleMapClick', 'raw event', {
        hasLatLng: !!e.latLng,
        eventType: e?.domEvent?.type,
        nativeEvent: e?.domEvent ? { type: e.domEvent.type, clientX: e.domEvent.clientX, clientY: e.domEvent.clientY } : null,
      });
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      LOG('handleMapClick', 'extracted coords', { lat, lng, latType: typeof lat, lngType: typeof lng });
      if (lat != null && lng != null) {
        const newPoint = { lat, lng };
        LOG('handleMapClick', 'adding waypoint', newPoint);
        if (manualSplineMode) manualRedoStackRef.current = [];
        setWaypoints((prev) => {
          const next = [...prev, newPoint];
          LOG('handleMapClick', 'waypoints after add', next);
          return next;
        });
        if (centerOnWaypointClick && mapRef.current) {
          mapRef.current.panTo(newPoint);
          // Cancel the pan animation the instant the user starts dragging so the
          // map immediately responds to mouse movement instead of fighting the animation.
          window.google.maps.event.addListenerOnce(mapRef.current, 'dragstart', () => {
            mapRef.current?.setCenter(mapRef.current.getCenter());
          });
        }
        setLoading(true);
        // Reverse geocode to get human-readable address (like "Directions from here" in Google Maps)
        if (window.google?.maps?.importLibrary) {
          window.google.maps.importLibrary('geocoding').then(({ Geocoder }) => {
            const geocoder = new Geocoder();
            geocoder.geocode({ location: { lat, lng } })
              .then((response) => {
                if (response.results?.[0]) {
                  const addr = response.results[0].formatted_address;
                  setWaypoints((prev) => prev.map((wp) =>
                    Math.abs(wp.lat - lat) < 1e-6 && Math.abs(wp.lng - lng) < 1e-6
                      ? { ...wp, address: addr }
                      : wp
                  ));
                }
              })
              .catch(() => { /* ignore geocode errors */ });
          }).catch(() => { /* ignore */ });
        }
      } else {
        LOG('handleMapClick', 'WARN: lat/lng null or undefined', { lat, lng });
      }
    },
    [allowAddWaypointOnClick, centerOnWaypointClick, setWaypoints, setLoading, manualSplineMode, traceSeedPickActive, onTraceSeedPicked]
  );

  useEffect(() => {
    if (!laneEditMode) setShowLaneEditGuide(false);
  }, [laneEditMode]);

  useEffect(() => {
    if (!showLaneEditGuide) return;

    function handlePointerDown(event) {
      if (laneEditHintRef.current?.contains(event.target)) return;
      if (laneEditGuideRef.current?.contains(event.target)) return;
      setShowLaneEditGuide(false);
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') setShowLaneEditGuide(false);
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showLaneEditGuide]);

  // Keyboard shortcuts for manual spline mode (Ctrl+Z undo, Ctrl+Y redo)
  useEffect(() => {
    if (!manualSplineMode || laneEditMode) return;
    function handleKey(e) {
      if (!e.ctrlKey && !e.metaKey) return;
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        setWaypoints((prev) => {
          if (prev.length === 0) return prev;
          manualRedoStackRef.current.push(prev[prev.length - 1]);
          return prev.slice(0, -1);
        });
      } else if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        const point = manualRedoStackRef.current.pop();
        if (point) setWaypoints((prev) => [...prev, point]);
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [manualSplineMode, laneEditMode, setWaypoints]);

  // All tiles have settled (loaded or permanently failed) once loaded >= total.
  const orthoAllSettled = orthoTilesTotal > 0 && orthoTilesLoaded >= orthoTilesTotal;
  const orthoShowFailed = !orthoFetchingMeta && orthoFailedCount > 0 && orthoAllSettled;
  const orthoBarVisible =
    orthoFetchingMeta ||
    (orthoTilesTotal > 0 && orthoTilesLoaded < orthoTilesTotal) ||
    orthoShowFailed;
  const orthoBarPercent =
    orthoTilesTotal > 0
      ? Math.min(100, Math.round((orthoTilesLoaded / orthoTilesTotal) * 100))
      : 0;

  return (
    <div className="path-selector-map-container">
      <div ref={mapWrapperRef} className="path-selector-map-wrapper path-selector-add-mode">
        {orthoBarVisible && (
          <div
            className="path-map-ortho-load-bar"
            role="status"
            aria-live="polite"
            aria-label="Loading ortho map tiles"
          >
            {orthoShowFailed ? (
              <>
                <i className="fas fa-triangle-exclamation" aria-hidden />
                <span className="path-map-ortho-load-bar-label">
                  {orthoFailedCount} ortho tile{orthoFailedCount > 1 ? 's' : ''} failed to load
                </span>
                <button
                  type="button"
                  className="path-map-ortho-retry-btn"
                  onClick={() => {
                    setOrthoFailedCount(0);
                    setOrthoRetryNonce((n) => n + 1);
                  }}
                >
                  Retry
                </button>
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt fa-spin" aria-hidden />
                <span className="path-map-ortho-load-bar-label">
                  {orthoFetchingMeta
                    ? 'Fetching ortho tile list…'
                    : `Loading ortho tiles ${orthoTilesLoaded} / ${orthoTilesTotal}`}
                </span>
                {!orthoFetchingMeta && orthoTilesTotal > 0 && (
                  <div className="path-map-ortho-load-bar-progress" aria-hidden>
                    <div
                      className="path-map-ortho-load-bar-fill"
                      style={{ width: `${orthoBarPercent}%` }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {pathsPanelMode === 'intersection' && !laneEditMode && !manualSplineMode && (
          <div className="path-map-intersection-tools" role="group" aria-label="Drawing tool">
            {[
              { value: null, label: 'Move', icon: 'fa-solid fa-hand-back-fist', title: 'Pan/move map (no drawing)' },
              { value: 'measure-distance', label: 'Measure', icon: 'fa-ruler', title: 'Measure distance (click points on map)' },
            ].map(({ value, label, icon, title }) => (
              <button
                key={value ?? 'null'}
                type="button"
                className={`path-map-intersection-tool-btn${intersectionDrawingMode === value ? ' path-map-intersection-tool-btn--active' : ''}`}
                onClick={() => onIntersectionDrawingModeChange?.(value)}
                title={title}
                aria-pressed={intersectionDrawingMode === value}
                aria-label={title}
              >
                <i className={icon.includes(' ') ? `fa ${icon}` : `fas ${icon}`} aria-hidden />
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
        {routeStageHint &&
          pathsPanelMode === 'path-creator' &&
          !laneEditMode &&
          !manualSplineMode && (
            <div
              className={`path-map-route-stage-hint path-map-route-stage-hint--${routeStageHint.variant}`}
              role="status"
              aria-live="polite"
            >
              {routeStageHint.variant !== 'info' && (
                <i
                  className={`${
                    routeStageHint.variant === 'done'
                      ? 'fa-solid fa-circle-check'
                      : 'fa-solid fa-arrow-right-long'
                  } path-map-route-stage-hint-icon`}
                  aria-hidden
                />
              )}
              <span>{routeStageHint.text}</span>
              <button
                type="button"
                className="path-map-route-stage-hint-info-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOrthoMapsInfo(true);
                }}
                title="What is an ortho map?"
                aria-label="What is an ortho map? Open explanation"
                aria-haspopup="dialog"
              >
                <i className="fa-solid fa-circle-info" aria-hidden />
              </button>
            </div>
          )}
        {laneEditMode && (
          <>
            <div
              ref={laneEditHintRef}
              className="path-map-lane-edit-hint"
              role="status"
              aria-live="polite"
            >
              Hold
              <span className="path-map-lane-edit-key">Ctrl</span>
              <span className="path-map-lane-edit-plus" aria-hidden>+</span>
              <span className="path-map-lane-edit-key path-map-lane-edit-key--mouse" aria-label="mouse drag">
                <i className="fa-solid fa-computer-mouse" aria-hidden />
              </span>
              and drag to select multiple points
              <button
                type="button"
                className="path-map-lane-edit-info-btn"
                title="Lane edit hint"
                aria-label="Lane edit hint"
                aria-expanded={showLaneEditGuide}
                aria-haspopup="dialog"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLaneEditGuide((prev) => !prev);
                }}
              >
                <i className="fa-solid fa-circle-info" aria-hidden />
              </button>
            </div>
            {/* ── Lane edit toolbar (top-right) ── */}
            <div className="path-map-lane-edit-toolbar" role="toolbar" aria-label="Lane edit tools">
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn"
                onClick={onLaneEditUndo}
                disabled={!laneEditCanUndo}
                title="Undo (Ctrl+Z)"
              >
                <i className="fa-solid fa-rotate-left" aria-hidden />
              </button>
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn"
                onClick={onLaneEditRedo}
                disabled={!laneEditCanRedo}
                title="Redo (Ctrl+Y)"
              >
                <i className="fa-solid fa-rotate-right" aria-hidden />
              </button>
              <div className="path-map-lane-edit-toolbar-sep" />
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn"
                onClick={onLaneEditSelectAll}
                title="Select All (Ctrl+A)"
              >
                <i className="fa-solid fa-object-group" aria-hidden />
              </button>
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn path-map-lane-edit-toolbar-btn--danger"
                onClick={onLaneEditDeleteSelected}
                disabled={!laneEditSelectedIndices?.length}
                title="Delete Selected (Del)"
              >
                <i className="fa-solid fa-trash-can" aria-hidden />
              </button>
              <span className="paths-list-edit-toolbar-count" title="Hold Ctrl and drag to lasso-select points">
                {(laneEditSelectedIndices?.length ?? 0)} point{(laneEditSelectedIndices?.length ?? 0) !== 1 ? "s" : ""} selected
              </span>
              {(laneEditSelectedIndices?.length ?? 0) > 0 && (
                <button
                  type="button"
                  className="paths-list-edit-toolbar-clear"
                  onClick={onLaneEditClearSelection}
                  title="Clear selection"
                  aria-label="Clear selection"
                >
                  <i className="fa-solid fa-xmark" aria-hidden />
                </button>
              )}
            </div>
            {showLaneEditGuide && (
              <>
                <button
                  type="button"
                  className="path-map-lane-edit-guide-backdrop"
                  aria-label="Close lane editing guide"
                  onClick={() => setShowLaneEditGuide(false)}
                />
                <div
                  ref={laneEditGuideRef}
                  className="path-map-lane-edit-guide"
                  role="dialog"
                  aria-modal="false"
                  aria-label="Lane editing guide"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="path-map-lane-edit-guide-header">
                    <p className="path-map-lane-edit-guide-title">Keyboard shortcuts</p>
                    <button
                      type="button"
                      className="path-map-lane-edit-guide-close"
                      onClick={() => setShowLaneEditGuide(false)}
                      title="Close guide"
                      aria-label="Close guide"
                    >
                      <i className="fa-solid fa-xmark" aria-hidden />
                    </button>
                  </div>

                  {/* ── Nodes ── */}
                  <p className="path-map-lane-edit-guide-section">Nodes</p>
                  <ul className="path-map-lane-edit-guide-list">
                    <li>
                      <span className="path-map-lane-edit-guide-text">Add node</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-guide-desc">Click the white circle on the lane line</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Move node</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-guide-desc">Drag a node marker</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Delete node</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-guide-desc">Right-click a node</span>
                      </span>
                    </li>
                  </ul>

                  {/* ── Selection ── */}
                  <p className="path-map-lane-edit-guide-section">Selection</p>
                  <ul className="path-map-lane-edit-guide-list">
                    <li>
                      <span className="path-map-lane-edit-guide-text">Select one</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-guide-desc">Click a node</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Add to selection</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Shift</span>
                        <span className="path-map-lane-edit-plus">+</span>
                        <span className="path-map-lane-edit-guide-desc">Click</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Lasso select</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Ctrl</span>
                        <span className="path-map-lane-edit-plus">+</span>
                        <span className="path-map-lane-edit-guide-desc">Drag</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Select all</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Ctrl</span>
                        <span className="path-map-lane-edit-plus">+</span>
                        <span className="path-map-lane-edit-key">A</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Clear selection</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Esc</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Move selected group</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-guide-desc">Drag any selected node</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Delete selected</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Del</span>
                      </span>
                    </li>
                  </ul>

                  {/* ── History ── */}
                  <p className="path-map-lane-edit-guide-section">History</p>
                  <ul className="path-map-lane-edit-guide-list">
                    <li>
                      <span className="path-map-lane-edit-guide-text">Undo</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Ctrl</span>
                        <span className="path-map-lane-edit-plus">+</span>
                        <span className="path-map-lane-edit-key">Z</span>
                      </span>
                    </li>
                    <li>
                      <span className="path-map-lane-edit-guide-text">Redo</span>
                      <span className="path-map-lane-edit-guide-shortcut" aria-hidden="true">
                        <span className="path-map-lane-edit-key">Ctrl</span>
                        <span className="path-map-lane-edit-plus">+</span>
                        <span className="path-map-lane-edit-key">Y</span>
                      </span>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </>
        )}
        {manualSplineMode && !laneEditMode && (
          <>
            <div
              className="path-map-lane-edit-hint"
              role="status"
              aria-live="polite"
            >
              <i className="fa-solid fa-pen path-map-lane-edit-hint-icon" aria-hidden />
              Manual lane mode — click on the map to add lane points
            </div>
            <div className="path-map-lane-edit-toolbar" role="toolbar" aria-label="Manual lane tools">
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn"
                onClick={() => {
                  setWaypoints((prev) => {
                    if (prev.length === 0) return prev;
                    const removed = prev[prev.length - 1];
                    manualRedoStackRef.current.push(removed);
                    return prev.slice(0, -1);
                  });
                }}
                disabled={!waypoints?.length}
                title="Undo last point (Ctrl+Z)"
              >
                <i className="fa-solid fa-rotate-left" aria-hidden />
              </button>
              <button
                type="button"
                className="path-map-lane-edit-toolbar-btn"
                onClick={() => {
                  const point = manualRedoStackRef.current.pop();
                  if (point) {
                    setWaypoints((prev) => [...prev, point]);
                  }
                }}
                disabled={!manualRedoStackRef.current.length}
                title="Redo point (Ctrl+Y)"
              >
                <i className="fa-solid fa-rotate-right" aria-hidden />
              </button>
            </div>
          </>
        )}
        {loadError && (
          <div className="path-map-error">
            Error loading Google Maps. Enable &quot;Maps JavaScript API&quot;, &quot;Directions API&quot;, &quot;Routes API&quot;, and &quot;Geocoding API&quot; in Google Cloud Console for this key.
          </div>
        )}
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={centerRef.current}
            zoom={initialZoom}
            onClick={handleMapClick}
            onLoad={onMapLoad}
            id="path-selector-google-map"
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              rotateControl: false,
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              scaleControl: true,
              tilt: 0,
              heading: 0,
              tiltInteractionEnabled: false,
              headingInteractionEnabled: false,
              gestureHandling: 'auto',
              draggableCursor: (manualSplineMode || traceSeedPickActive) ? 'crosshair' : 'grab',
              draggingCursor: (manualSplineMode || traceSeedPickActive) ? 'crosshair' : 'grabbing',
              styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
            }}
          >
            <MapResizer isVisible={isVisible} />
            <ImageOverlays imageOverlays={imageOverlays} overlayOpacity={overlayOpacity} overlayBrightness={overlayBrightness} overlayContrast={overlayContrast} onLoadProgress={onImageOverlaysLoadProgress} onFailedChange={setOrthoFailedCount} retryNonce={orthoRetryNonce} />
            <LasBoundingBoxOverlays
              lasBoundingBoxes={lasBoundingBoxes}
              selectedIndex={selectedLasBoundingBoxIndex}
            />
            <MapSearchBox
              pathsPanelMode={pathsPanelMode}
              onPathsPanelModeChange={onPathsPanelModeChange}
              intersectionDrawingMode={intersectionDrawingMode}
              measurePoints={measurePoints}
              onMeasurePointsChange={onMeasurePointsChange}
            />
            <MapDrawingLayer
              pathsPanelMode={pathsPanelMode}
              drawingMode={intersectionDrawingMode}
              onOverlayComplete={onIntersectionOverlayComplete}
              onIntersectionOverlayUpdate={onIntersectionOverlayUpdate}
              onIntersectionOverlayRemove={onIntersectionOverlayRemove}
              intersectionOverlays={intersectionOverlays}
              curvePoints={curvePoints}
              onCurvedLinePointsChange={onCurvedLinePointsChange}
              measurePoints={measurePoints}
              onMeasurePointsChange={onMeasurePointsChange}
              curvedLineEditPointCount={curvedLineEditPointCount}
              intersections={intersections}
              activeIntersectionId={activeIntersectionId}
              selectedTargetId={selectedTargetId}
              onAddIntersection={onAddIntersection}
              onUpdateIntersection={onUpdateIntersection}
              onSetActiveIntersection={onSetActiveIntersection}
              pendingIsdDraft={pendingIsdDraft}
              onCancelPendingIsdDraft={onCancelPendingIsdDraft}
              oppositePointPickActive={oppositePointPickActive}
              onOppositeBlockPicked={onOppositeBlockPicked}
              onCancelOppositePointPick={onCancelOppositePointPick}
              oppositeBlockDraft={oppositeBlockDraft}
              onUpdateOppositeBlockDraft={onUpdateOppositeBlockDraft}
            />
            <PathMapContent
              waypoints={waypoints}
              setWaypoints={setWaypoints}
              onPathChange={onPathChange}
              setLoading={setLoading}
              waypointColor={waypointColor}
              fullPathStrokeColor={fullPathStrokeColor}
              fullPathPathData={fullPathPathData}
              fitToPathData={fitToPathData}
              onFitToPathComplete={onFitToPathComplete}
              fitToBounds={fitToBounds}
              onFitToBoundsComplete={onFitToBoundsComplete}
              segmentVisibility={segmentVisibility}
              activeSegmentIndex={activeSegmentIndex}
              highlightPathData={highlightPathData}
              highlightColor={highlightColor}
              activeSegmentPathData={activeSegmentPathData}
              activeEntityId={activeEntityId}
              freezeActiveSegmentPath={freezeActiveSegmentPath}
              manualSplineMode={manualSplineMode}
              persistentPathOverlays={persistentPathOverlays}
              directionsResult={directionsResult}
              setDirectionsResult={setDirectionsResult}
              isEditMode={true}
              waypointsLocked={waypointsLocked}
              laneEditMode={laneEditMode}
              laneEditWaypoints={laneEditWaypoints}
              laneEditSelectedIndices={laneEditSelectedIndices}
              laneEditMarkerColor={laneEditMarkerColor}
              onLaneEditSelectionChange={onLaneEditSelectionChange}
              onLaneEditWaypointsMoved={onLaneEditWaypointsMoved}
              onLaneEditWaypointInserted={onLaneEditWaypointInserted}
              onLaneEditWaypointRemoved={onLaneEditWaypointRemoved}
              laneEditCanUndo={laneEditCanUndo}
              laneEditCanRedo={laneEditCanRedo}
              onLaneEditUndo={onLaneEditUndo}
              onLaneEditRedo={onLaneEditRedo}
              onLaneEditDeleteSelected={onLaneEditDeleteSelected}
              onLaneEditSelectAll={onLaneEditSelectAll}
              onLaneEditClearSelection={onLaneEditClearSelection}
              speedEditMode={speedEditMode}
              speedEditWaypoints={speedEditWaypoints}
              speedEditSegments={speedEditSegments}
              onSpeedBreakpointAdded={onSpeedBreakpointAdded}
            />
          </GoogleMap>
        )}
      </div>
      <OrthoMapsInfoModal
        open={showOrthoMapsInfo}
        onClose={() => setShowOrthoMapsInfo(false)}
      />
    </div>
  );
}

const ROUTE_POLYLINE_OPTIONS = {
  strokeColor: '#460FFB',
  strokeWeight: 10,
  strokeOpacity: 0.9,
};
const ROUTE_POLYLINE_BORDER = {
  strokeColor: '#000000',
  strokeWeight: 12,
  strokeOpacity: 1,
};
const MAX_MAIN_STROKE = 10;
const MAX_BORDER_STROKE = 11;
const MIN_MAIN_STROKE = 4;
const MIN_BORDER_STROKE = 5;
const ZOOM_LEVEL_MAX = 1;
const ZOOM_LEVEL_MIN = 10;

function clearRoutePolylinesRef(ref) {
  const curr = ref.current;
  if (!curr) return;
  const polylines = Array.isArray(curr) ? curr : (curr.polylines && Array.isArray(curr.polylines) ? curr.polylines : []);
  polylines.forEach((p) => (p && typeof p.setMap === 'function') && p.setMap(null));
  if (curr.hoverMarker && typeof curr.hoverMarker.setMap === 'function') {
    curr.hoverMarker.setMap(null);
  }
  if (curr.zoomListener && typeof window.google?.maps?.event?.removeListener === 'function') {
    window.google.maps.event.removeListener(curr.zoomListener);
  }
  ref.current = [];
}

function getStrokeWeightsForZoom(zoom) {
  if (zoom >= ZOOM_LEVEL_MAX) {
    return { main: MIN_MAIN_STROKE, border: MIN_BORDER_STROKE };
  }
  if (zoom <= ZOOM_LEVEL_MIN) {
    return { main: MAX_MAIN_STROKE, border: MAX_BORDER_STROKE };
  }
  const t = (zoom - ZOOM_LEVEL_MIN) / (ZOOM_LEVEL_MAX - ZOOM_LEVEL_MIN);
  return {
    main: Math.round(MAX_MAIN_STROKE - t * (MAX_MAIN_STROKE - MIN_MAIN_STROKE)),
    border: Math.round(MAX_BORDER_STROKE - t * (MAX_BORDER_STROKE - MIN_BORDER_STROKE)),
  };
}

/** Safely get lat/lng from a path point (LatLng with .lat()/.lng() or plain { lat, lng }) */
function getPathPointLatLng(p) {
  if (p == null) return { lat: undefined, lng: undefined };
  const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
  const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
  return { lat, lng };
}

/** Closest point on the path (segment polyline) to the given lat,lng */
function closestPointOnPath(path, lat, lng) {
  const pts = path.map((p) => getPathPointLatLng(p)).filter((p) => p.lat != null && p.lng != null);
  if (pts.length < 2) return pts[0] || null;
  let best = null;
  let bestDist = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const ax = a.lng;
    const ay = a.lat;
    const bx = b.lng;
    const by = b.lat;
    const px = lng;
    const py = lat;
    const dx = bx - ax;
    const dy = by - ay;
    const d2 = dx * dx + dy * dy;
    let t = d2 <= 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / d2;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx;
    const qy = ay + t * dy;
    const dist = (qx - px) ** 2 + (qy - py) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = { lat: qy, lng: qx };
    }
  }
  return best;
}

/** True when the two waypoints sit on the stored path ends (generated lane not yet reshaped). */
function waypointsMatchPathEndpoints(waypoints, fixedPath, tolDeg = 1e-5) {
  if (!Array.isArray(waypoints) || waypoints.length !== 2 || !Array.isArray(fixedPath) || fixedPath.length < 2) {
    return false;
  }
  const a0 = getPathPointLatLng(fixedPath[0]);
  const a1 = getPathPointLatLng(fixedPath[fixedPath.length - 1]);
  const w0 = waypoints[0];
  const w1 = waypoints[1];
  const near = (p, q) =>
    p &&
    q &&
    Number.isFinite(p.lat) &&
    Number.isFinite(q.lat) &&
    Number.isFinite(p.lng) &&
    Number.isFinite(q.lng) &&
    Math.abs(p.lat - q.lat) < tolDeg &&
    Math.abs(p.lng - q.lng) < tolDeg;
  if (near(w0, a0) && near(w1, a1)) return true;
  if (near(w0, a1) && near(w1, a0)) return true;
  return false;
}

function findInsertIndexForClick(path, waypoints, clickLat, clickLng) {
  if (path.length < 2 || waypoints.length < 2) return null;
  let closestPathIdx = 0;
  let minDist = Infinity;
  for (let i = 0; i < path.length; i++) {
    const { lat, lng } = getPathPointLatLng(path[i]);
    if (lat == null || lng == null) continue;
    const d = (lat - clickLat) ** 2 + (lng - clickLng) ** 2;
    if (d < minDist) {
      minDist = d;
      closestPathIdx = i;
    }
  }
  const pathLen = path.length;
  const segments = waypoints.length - 1;
  const ptsPerSegment = Math.max(1, Math.floor(pathLen / segments));
  const insertAfter = Math.min(
    Math.floor(closestPathIdx / ptsPerSegment),
    waypoints.length - 2
  );
  return insertAfter + 1;
}

function addRoutePolylines(map, path, ref, opts = {}) {
  const {
    waypoints = [],
    setWaypoints,
    setLoading,
    routeStrokeColor,
    enableWaypointInsertionOnPath = false,
  } = opts;
  const mainColor = routeStrokeColor ?? ROUTE_POLYLINE_OPTIONS.strokeColor;
  clearRoutePolylinesRef(ref);

  const pathNorm = path.map((p) => getPathPointLatLng(p)).filter((p) => p.lat != null && p.lng != null);
  const zoom = map.getZoom() ?? 12;
  const { main: mainW, border: borderW } = getStrokeWeightsForZoom(zoom);
  const border = new window.google.maps.Polyline({
    path,
    strokeColor: ROUTE_POLYLINE_BORDER.strokeColor,
    strokeWeight: borderW,
    strokeOpacity: ROUTE_POLYLINE_BORDER.strokeOpacity,
    map,
    clickable: !!enableWaypointInsertionOnPath,
    zIndex: 220,
  });
  const main = new window.google.maps.Polyline({
    path,
    strokeColor: mainColor,
    strokeWeight: mainW,
    strokeOpacity: ROUTE_POLYLINE_OPTIONS.strokeOpacity,
    map,
    clickable: !!enableWaypointInsertionOnPath,
    zIndex: 221,
  });

  // Wider invisible hit area so the hover/drag point is easier to trigger
  const HIT_AREA_STROKE = 20;
  const hitArea =
    pathNorm.length >= 2 && setWaypoints && enableWaypointInsertionOnPath
      ? new window.google.maps.Polyline({
          path,
          strokeColor: '#000000',
          strokeWeight: HIT_AREA_STROKE,
          strokeOpacity: 0,
          map,
          clickable: true,
          zIndex: 222,
        })
      : null;

  let isDragging = false;
  const hoverMarker =
    pathNorm.length >= 2 && setWaypoints && enableWaypointInsertionOnPath
      ? new window.google.maps.Marker({
          position: pathNorm[0],
          map,
          visible: false,
          draggable: true,
          cursor: 'pointer',
          zIndex: 230,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 3,
            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: '#000000',
            strokeWeight: 2,
          },
        })
      : null;

  if (hoverMarker && hitArea && setWaypoints && waypoints.length >= 2) {
    window.google.maps.event.addListener(hitArea, 'mousemove', (e) => {
      if (isDragging) return;
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      const pt = closestPointOnPath(path, lat, lng);
      if (pt) {
        hoverMarker.setPosition(pt);
        hoverMarker.setVisible(true);
      }
    });
    window.google.maps.event.addListener(hitArea, 'mouseout', () => {
      if (!isDragging) hoverMarker.setVisible(false);
    });
    window.google.maps.event.addListener(hoverMarker, 'dragstart', () => {
      isDragging = true;
    });
    window.google.maps.event.addListener(hoverMarker, 'dragend', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      isDragging = false;
      hoverMarker.setVisible(false);
      if (lat == null || lng == null) return;
      const insertIdx = findInsertIndexForClick(pathNorm, waypoints, lat, lng);
      if (insertIdx != null) {
        setWaypoints((prev) => {
          const next = [...prev];
          next.splice(insertIdx, 0, { lat, lng });
          return next;
        });
        setLoading?.(true);
      }
    });
  }

  if (setWaypoints && waypoints.length >= 2 && enableWaypointInsertionOnPath) {
    const clickPoly = hitArea || main;
    window.google.maps.event.addListener(clickPoly, 'click', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat == null || lng == null) return;
      const insertIdx = findInsertIndexForClick(pathNorm, waypoints, lat, lng);
      if (insertIdx != null) {
        setWaypoints((prev) => {
          const next = [...prev];
          next.splice(insertIdx, 0, { lat, lng });
          return next;
        });
        setLoading?.(true);
      }
    });
  }
  const updateWeights = () => {
    const z = map.getZoom() ?? 12;
    const { main: mw, border: bw } = getStrokeWeightsForZoom(z);
    border.setOptions({ strokeWeight: bw });
    main.setOptions({ strokeWeight: mw });
  };
  const zoomListener = window.google.maps.event.addListener(map, 'zoom_changed', updateWeights);
  ref.current = { polylines: hitArea ? [border, main, hitArea] : [border, main], zoomListener, hoverMarker };
}

/** Single waypoint marker icon; color matches segment indicator */
function getWaypointMarkerIcon(fillColor = '#DD4130') {
  const fill = fillColor;
  const stroke = '#000000';
  const strokeWidth = 10;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`;
  const g = window.google?.maps;
  const w = 24;
  const h = 31;
  return { url: 'data:image/svg+xml,' + encodeURIComponent(svg), scaledSize: g ? new g.Size(w, h) : { width: w, height: h }, anchor: g ? new g.Point(w / 2, h) : { x: w / 2, y: h } };
}

/** Crosshair icon for intersection drawing "Point" tool (anchor at center) */
function getDrawingPointMarkerIcon(stroke = '#DD4130') {
  const border = '#000000';
  // Cross as four arms that stop at circle edge (r=6, center 12,12): arms from 2 to 6 and 18 to 20
  const crossD = `M12 2v4 M12 18v4 M2 12h4 M18 12h4`;
  // Small 2px center dot (r=1 in viewBox 24) on top
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="none" stroke="${border}" stroke-width="3" stroke-linecap="round" d="${crossD}"/><circle fill="none" stroke="${border}" stroke-width="2.5" cx="12" cy="12" r="6"/><path fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" d="${crossD}"/><circle fill="none" stroke="${stroke}" stroke-width="1.5" cx="12" cy="12" r="6"/><circle fill="${stroke}" stroke="${border}" stroke-width="0.5" cx="12" cy="12" r="1"/></svg>`;
  const g = window.google?.maps;
  const w = 24;
  const h = 24;
  return { url: 'data:image/svg+xml,' + encodeURIComponent(svg), scaledSize: g ? new g.Size(w, h) : { width: w, height: h }, anchor: g ? new g.Point(w / 2, h / 2) : { x: w / 2, y: h / 2 } };
}

/** Small white circle marker used by the measure tool points */
function getMeasurePointMarkerIcon(g) {
  return {
    path: g.SymbolPath.CIRCLE,
    scale: 4.5,
    fillColor: '#ffffff',
    fillOpacity: 1,
    strokeColor: '#000000',
    strokeWeight: 2,
  };
}

/** Small circle node for middle waypoints */
function getWaypointNodeIcon(fillColor = '#DD4130') {
  const g = window.google?.maps;
  return {
    path: g ? g.SymbolPath.CIRCLE : 0,
    scale: 6,
    fillColor,
    fillOpacity: 1,
    strokeColor: '#000000',
    strokeWeight: 1.5,
  };
}

/** Hover state for middle waypoint nodes */
function getWaypointNodeIconHover(fillColor = '#DD4130') {
  const g = window.google?.maps;
  return {
    path: g ? g.SymbolPath.CIRCLE : 0,
    scale: 8,
    fillColor,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2,
  };
}

/** Waypoint marker icon with border for hover state */
function getWaypointMarkerIconHover(fillColor = '#DD4130') {
  const fill = fillColor;
  const stroke = '#000000';
  const strokeWidth = 30;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>`;
  const g = window.google?.maps;
  const w = 24;
  const h = 31;
  return { url: 'data:image/svg+xml,' + encodeURIComponent(svg), scaledSize: g ? new g.Size(w, h) : { width: w, height: h }, anchor: g ? new g.Point(w / 2, h) : { x: w / 2, y: h } };
}

/** Inner content: Route.computeRoutes (new API) + polyline + markers */
function PathMapContent(props) {
  const {
    waypoints,
    setWaypoints,
    onPathChange,
    setLoading,
    waypointColor = '#DD4130',
    fullPathStrokeColor = '#DD4130',
    fullPathPathData = null,
    fitToPathData = null,
    onFitToPathComplete,
    fitToBounds = null,
    onFitToBoundsComplete,
    segmentVisibility = [],
    activeSegmentIndex = 0,
    activeEntityId = null,
    highlightPathData = null,
    highlightColor = '#f59e0b',
    activeSegmentPathData = null,
    freezeActiveSegmentPath = false,
    manualSplineMode = false,
    persistentPathOverlays = [],
    directionsResult,
    setDirectionsResult,
    isEditMode,
    waypointsLocked = false,
    laneEditMode = false,
    laneEditWaypoints = [],
    laneEditSelectedIndices = [],
    laneEditMarkerColor = '#2563eb',
    onLaneEditSelectionChange,
    onLaneEditWaypointsMoved,
    onLaneEditWaypointInserted,
    onLaneEditWaypointRemoved,
    laneEditCanUndo = false,
    laneEditCanRedo = false,
    onLaneEditUndo,
    onLaneEditRedo,
    onLaneEditDeleteSelected,
    onLaneEditSelectAll,
    speedEditMode = false,
    speedEditWaypoints = [],
    speedEditSegments = [],
    onSpeedBreakpointAdded,
  } = props;

  const map = useGoogleMap();
  const routePolylinesRef = useRef([]);
  const persistentPolylinesRef = useRef([]);
  const waypointMarkersRef = useRef([]);
  const highlightPolylineRef = useRef(null);
  const fullPathPolylineRef = useRef(null);
  const prevWaypointsLengthRef = useRef(-1);
  // Tracks which entity (path/lane id) the effect last ran for, so we can tell a
  // genuine in-place waypoint deletion apart from an active-entity SWITCH. Both make
  // waypoints drop below 2, but only a real deletion should clear the path's stored
  // geometry. On a switch the parent's onPathChange targets the PREVIOUS entity (its
  // ref updates in a later effect), so emitting here would wipe the wrong route's path.
  const prevEntityIdRef = useRef(null);
  // Tracks which waypointsList reference last triggered onPathChange in the freeze branch.
  // Prevents the render loop: onPathChange → activeSegmentPathData changes (new object) →
  // effect re-runs (activeSegmentPathData is in deps) → onPathChange again → ∞
  const lastFreezeEmitWaypointsRef = useRef(null);
  const waypointsList = Array.isArray(waypoints) ? waypoints : [];
  const isActiveSegmentVisible = segmentVisibility[activeSegmentIndex] !== false;
  // The standard waypoint editor and the lane point editor both render draggable markers.
  // Keep them mutually exclusive or they fight for the same click/drag events.
  const canEditWaypoints = Boolean(isEditMode) && !laneEditMode && !waypointsLocked;

  // Fit map view to path when fitToPathData is set (go-to segment)
  useEffect(() => {
    if (!map || !window.google?.maps || !fitToPathData?.geometry?.coordinates?.length) return;
    const coords = fitToPathData.geometry.coordinates;
    const points = coords.map((c) => {
      const lng = Array.isArray(c) ? c[0] : c.lng;
      const lat = Array.isArray(c) ? c[1] : c.lat;
      return { lat, lng };
    });
    if (points.length >= 2) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    } else if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(14);
    }
    onFitToPathComplete?.();
  }, [map, fitToPathData, onFitToPathComplete]);

  // Fit map view to bounds when fitToBounds is set (go-to LAS extent)
  useEffect(() => {
    if (!map || !window.google?.maps || !fitToBounds) return;
    const b = fitToBounds;
    if (typeof b.south === 'number' && typeof b.north === 'number' && typeof b.west === 'number' && typeof b.east === 'number') {
      const bounds = new window.google.maps.LatLngBounds(
        { lat: b.south, lng: b.west },
        { lat: b.north, lng: b.east }
      );
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    }
    onFitToBoundsComplete?.();
  }, [map, fitToBounds, onFitToBoundsComplete]);

  useEffect(() => {
    LOG('PathMapContent', 'effect run', { hasMap: !!map, waypointsCount: waypointsList.length, waypoints: waypointsList });
    if (!map || !window.google?.maps?.importLibrary) {
      LOG('PathMapContent', 'effect skip (no map or importLibrary)');
      return;
    }
    // A waypoints drop below 2 can mean either the user deleted points from the
    // active path, or the active path SWITCHED to a different (e.g. empty) one.
    // Only the former should clear the stored geometry — guard the onPathChange(null)
    // emits below with !entityChanged so a switch never wipes the previous route's path.
    const entityChanged = prevEntityIdRef.current !== activeEntityId;
    prevEntityIdRef.current = activeEntityId;
    const fixedCoords =
      freezeActiveSegmentPath || waypointsList.length < 2
        ? activeSegmentPathData?.geometry?.coordinates
        : null;
    const fixedPath = Array.isArray(fixedCoords)
      ? fixedCoords
        .map((c) => {
          const lng = Array.isArray(c) ? c[0] : c?.lng;
          const lat = Array.isArray(c) ? c[1] : c?.lat;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean)
      : [];

    if (!isActiveSegmentVisible) {
      clearRoutePolylinesRef(routePolylinesRef);
      setDirectionsResult(null);
      setLoading(false);
      prevWaypointsLengthRef.current = waypointsList.length;
      return;
    }

    if (freezeActiveSegmentPath) {
      clearRoutePolylinesRef(routePolylinesRef);
      setDirectionsResult(null);
      setLoading(false);
      const hasFixedPath = fixedPath.length >= 2;
      if (waypointsList.length >= 2) {
        const directPath = waypointsList.map((w) => ({ lat: w.lat, lng: w.lng }));
        const useBaseline =
          hasFixedPath &&
          waypointsList.length === 2 &&
          waypointsMatchPathEndpoints(waypointsList, fixedPath);
        const displayPath =
          useBaseline
            ? fixedPath
            : directPath.length >= 3
              ? catmullRomSpline(directPath, SPLINE_SAMPLES_EDIT)
              : directPath;
        addRoutePolylines(map, displayPath, routePolylinesRef, {
          waypoints: waypointsList,
          setWaypoints: canEditWaypoints ? setWaypoints : null,
          setLoading,
          routeStrokeColor: waypointColor,
          // manualSplineMode: user adds points by clicking the map background, not by
          // clicking the polyline — disable the invisible hit-area so it doesn't
          // swallow mousedown events and break map panning.
          enableWaypointInsertionOnPath: !manualSplineMode && !speedEditMode,
        });
        const shouldPersistGeometry =
          manualSplineMode ||
          !useBaseline ||
          waypointsList.length > 2 ||
          !hasFixedPath;
        // Only emit when waypoints actually changed. Without this guard, calling
        // onPathChange updates activeSegmentPathData (new object ref), which is in
        // the effect deps, which re-runs the effect, which calls onPathChange again — ∞ loop.
        // waypointsList gets a new array reference only when the user adds/removes a point,
        // so comparing by reference is a correct and O(1) change-detection.
        if (shouldPersistGeometry && lastFreezeEmitWaypointsRef.current !== waypointsList) {
          lastFreezeEmitWaypointsRef.current = waypointsList;
          // Skip persisting geometry on an entity SWITCH. The parent's onPathChange
          // resolves its target via a ref that updates in a LATER effect, so during
          // the switch render it still points at the previously-active lane/path —
          // emitting here would write THIS entity's geometry into the previous one
          // (the cross-lane mixing). The line is already drawn above; persistence is
          // deferred until the user actually edits this entity (waypointsList changes
          // again, by which point the ref has caught up).
          if (!entityChanged) {
            const coords = displayPath.map((w) => {
              const { lat, lng } = getPathPointLatLng(w);
              return [lng, lat];
            });
            const samplePath = displayPath
              .map((w) => getPathPointLatLng(w))
              .filter((p) => p.lat != null && p.lng != null);
            const prevProps =
              activeSegmentPathData?.properties && typeof activeSegmentPathData.properties === 'object'
                ? activeSegmentPathData.properties
                : {};
            onPathChange({
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: coords },
              properties: {
                ...prevProps,
                pointsEvery10Feet: getPathCoordinatesEveryFeet(samplePath, 10),
              },
            });
          }
        }
      } else if (hasFixedPath) {
        addRoutePolylines(map, fixedPath, routePolylinesRef, {
          waypoints: [],
          setWaypoints: null,
          setLoading,
          routeStrokeColor: waypointColor,
        });
      }
      prevWaypointsLengthRef.current = waypointsList.length;
      return () => {
        clearRoutePolylinesRef(routePolylinesRef);
      };
    }

    if (fixedPath.length >= 2 && waypointsList.length < 2) {
      clearRoutePolylinesRef(routePolylinesRef);
      setDirectionsResult(null);
      setLoading(false);
      // If the user just deleted the waypoints (dropping from >=2 to <2), the
      // lingering activeSegmentPathData is stale — clear it instead of redrawing a
      // ghost path. Notifying the parent (onPathChange(null)) wipes the stored
      // pathData so the route line disappears immediately. We only redraw the stored
      // path when this wasn't an in-editor deletion (e.g. a saved segment being
      // displayed before its waypoints are loaded).
      if (prevWaypointsLengthRef.current >= 2 && !entityChanged) {
        onPathChange(null);
        prevWaypointsLengthRef.current = waypointsList.length;
        return;
      }
      addRoutePolylines(map, fixedPath, routePolylinesRef, {
        waypoints: [],
        setWaypoints: null,
        setLoading,
        routeStrokeColor: waypointColor,
      });
      prevWaypointsLengthRef.current = waypointsList.length;
      return () => {
        clearRoutePolylinesRef(routePolylinesRef);
      };
    }

    if (waypointsList.length < 2) {
      clearRoutePolylinesRef(routePolylinesRef);
      setDirectionsResult(null);
      setLoading(false);
      // Only notify parent when transitioning from 2+ waypoints to <2 to avoid update loop.
      // Skip on an entity switch (entityChanged) — onPathChange would target the previously
      // active route (stale parent ref) and wipe its stored road geometry.
      if (prevWaypointsLengthRef.current >= 2 && !entityChanged) {
        LOG('PathMapContent', 'waypoints < 2, clearing path');
        onPathChange(null);
      }
      prevWaypointsLengthRef.current = waypointsList.length;
      return;
    }

    prevWaypointsLengthRef.current = waypointsList.length;

    let cancelled = false;
    setLoading(true);

    const directPath = waypointsList.map((w) => ({ lat: w.lat, lng: w.lng }));
    const polylineOpts = {
      waypoints: waypointsList,
      setWaypoints: canEditWaypoints ? setWaypoints : null,
      setLoading,
      routeStrokeColor: waypointColor,
      // Prevent polyline hit-area from hijacking map drag in regular path-creator mode.
      enableWaypointInsertionOnPath: false,
    };
    const drawDirectPolyline = () => {
      addRoutePolylines(map, directPath, routePolylinesRef, polylineOpts);
      onPathChange({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: directPath.map((w) => [w.lng, w.lat]) },
        properties: { pointsEvery10Feet: getPathCoordinatesEveryFeet(directPath, 10) },
      });
    };

    const tryDirectionsService = () => {
      const origin = waypointsList[0];
      const destination = waypointsList[waypointsList.length - 1];
      const ds = new window.google.maps.DirectionsService();
      const waypointsForDir = waypointsList.slice(1, -1).map((w) => ({ location: { lat: w.lat, lng: w.lng }, stopover: true }));
      ds.route(
        {
          origin: { lat: origin.lat, lng: origin.lng },
          destination: { lat: destination.lat, lng: destination.lng },
          waypoints: waypointsForDir,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (cancelled) return;
          setLoading(false);
          if (status !== window.google.maps.DirectionsStatus.OK) {
            drawDirectPolyline();
            return;
          }
          const route = result.routes[0];
          const path = [];
          route.legs.forEach((leg) => {
            leg.steps.forEach((step) => {
              step.path.forEach((p) => path.push({ lat: p.lat(), lng: p.lng() }));
            });
          });
          const coordinates = path.map((p) => [p.lng, p.lat]);
          const feature = {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates },
            properties: { pointsEvery10Feet: getPathCoordinatesEveryFeet(path, 10) },
          };
          onPathChange(feature);
          addRoutePolylines(map, path, routePolylinesRef, polylineOpts);
        }
      );
    };

    LOG('PathMapContent', 'loading routes library');
    window.google.maps.importLibrary('routes').then(({ Route }) => {
      if (cancelled) return;
      const origin = waypointsList[0];
      const destination = waypointsList[waypointsList.length - 1];
      const intermediates = waypointsList.slice(1, -1).map((w) => ({ lat: w.lat, lng: w.lng }));

      const request = {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: 'DRIVING',
        fields: ['path', 'legs'],
      };
      if (intermediates.length > 0) {
        request.intermediates = intermediates;
      }

      LOG('PathMapContent', 'Route.computeRoutes request', request);
      Route.computeRoutes(request)
        .then(({ routes }) => {
          if (cancelled) return;
          LOG('PathMapContent', 'Route.computeRoutes response', { routesCount: routes?.length, firstRoutePathLen: routes?.[0]?.path?.length });
          setLoading(false);
          if (routes?.[0]) {
            const route = routes[0];
            setDirectionsResult(route);
            const path = route.path || [];
            const pathCoords = path.map((p) => getPathPointLatLng(p)).filter((p) => p.lat != null && p.lng != null);
            const coordinates = pathCoords.map((p) => [Number(p.lng) || 0, Number(p.lat) || 0]);
            const feature = {
              type: 'Feature',
              geometry: { type: 'LineString', coordinates },
              properties: { pointsEvery10Feet: getPathCoordinatesEveryFeet(pathCoords, 10) },
            };
            onPathChange(feature);
            clearRoutePolylinesRef(routePolylinesRef);
            try {
              if (pathCoords.length >= 2) {
                addRoutePolylines(map, pathCoords, routePolylinesRef, polylineOpts);
              }
            } catch (e) {
              if (pathCoords.length >= 2) {
                addRoutePolylines(map, pathCoords, routePolylinesRef, polylineOpts);
              }
            }
          } else {
            setDirectionsResult(null);
            tryDirectionsService();
          }
        })
        .catch((err) => {
          if (!cancelled) {
            LOG('PathMapContent', 'Route.computeRoutes ERROR', err?.message || err, err);
            setLoading(false);
            setDirectionsResult(null);
            tryDirectionsService();
          }
        });
    }).catch((err) => {
      if (!cancelled) {
        LOG('PathMapContent', 'Routes library load ERROR', err?.message || err, err);
        setLoading(false);
        setDirectionsResult(null);
        tryDirectionsService();
      }
    });

    return () => {
      cancelled = true;
      clearRoutePolylinesRef(routePolylinesRef);
    };
  }, [
    map,
    waypointsList,
    onPathChange,
    setLoading,
    setDirectionsResult,
    waypointColor,
    isActiveSegmentVisible,
    freezeActiveSegmentPath || waypointsList.length < 2 ? activeSegmentPathData : null,
    freezeActiveSegmentPath,
    manualSplineMode,
    speedEditMode,
    setWaypoints,
    canEditWaypoints,
    activeEntityId,
  ]);

  // Waypoint markers: draggable only on hover (created imperatively so we can use mouseover/mouseout)
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    const icon = getWaypointMarkerIcon(waypointColor);
    const iconHover = getWaypointMarkerIconHover(waypointColor);
    const nodeIcon = getWaypointNodeIcon(waypointColor);
    const nodeIconHover = getWaypointNodeIconHover(waypointColor);
    const prev = waypointMarkersRef.current;
    prev.forEach(({ marker, listeners }) => {
      listeners?.forEach((l) => g.event?.removeListener?.(l));
      marker.setMap(null);
    });
    waypointMarkersRef.current = [];

    if (!canEditWaypoints || !isActiveSegmentVisible) return () => {};

    const iconWithLabelOrigin = { ...icon, labelOrigin: new g.Point(25, -2) };
    const iconHoverWithLabelOrigin = { ...iconHover, labelOrigin: new g.Point(25, -2) };
    const lastIdx = waypointsList.length - 1;
    waypointsList.forEach((wp, i) => {
      const isEndpoint = i === 0 || i === lastIdx;
      const normalIcon = isEndpoint ? iconWithLabelOrigin : nodeIcon;
      const hoverIcon  = isEndpoint ? iconHoverWithLabelOrigin : nodeIconHover;
      const marker = new g.Marker({
        position: { lat: wp.lat, lng: wp.lng },
        map,
        icon: normalIcon,
        label: isEndpoint ? {
          text: String(i + 1),
          color: waypointColor,
          fontWeight: '800',
          fontSize: '13px',
        } : null,
        draggable: false,
        cursor: 'pointer',
      });
      const listeners = [];
      listeners.push(g.event.addListener(marker, 'mouseover', () => {
        marker.setOptions({ draggable: true, icon: hoverIcon });
      }));
      listeners.push(g.event.addListener(marker, 'mouseout', () => {
        marker.setOptions({ draggable: false, icon: normalIcon });
      }));
      listeners.push(g.event.addListener(marker, 'dragend', (e) => {
        const lat = e.latLng?.lat();
        const lng = e.latLng?.lng();
        if (lat != null && lng != null) {
          setWaypoints((prevWp) => {
            const next = [...prevWp];
            next[i] = { lat, lng };
            return next;
          });
          setLoading(true);
          if (window.google?.maps?.importLibrary) {
            window.google.maps.importLibrary('geocoding').then(({ Geocoder }) => {
              const geocoder = new Geocoder();
              geocoder.geocode({ location: { lat, lng } })
                .then((response) => {
                  if (response.results?.[0]) {
                    const addr = response.results[0].formatted_address;
                    setWaypoints((prevWp) => prevWp.map((w, idx) => (idx === i ? { ...w, address: addr } : w)));
                  }
                })
                .catch(() => {});
            }).catch(() => {});
          }
        }
      }));
      listeners.push(g.event.addListener(marker, 'rightclick', (e) => {
        e.domEvent?.preventDefault?.();
        setWaypoints((prevWp) => prevWp.filter((_, idx) => idx !== i));
        setLoading(true);
      }));
      waypointMarkersRef.current.push({ marker, listeners });
    });

    return () => {
      waypointMarkersRef.current.forEach(({ marker, listeners }) => {
        listeners?.forEach((l) => g.event?.removeListener?.(l));
        marker.setMap(null);
      });
      waypointMarkersRef.current = [];
    };
  }, [map, waypointsList, setWaypoints, setLoading, waypointColor, canEditWaypoints, isActiveSegmentVisible]);

  // Lane point editor — rubber-band select + group drag move + insert/remove
  useLanePointEditor({
    map,
    enabled: laneEditMode,
    waypoints: laneEditWaypoints,
    selectedIndices: laneEditSelectedIndices,
    markerColor: laneEditMarkerColor,
    onSelectionChange: onLaneEditSelectionChange ?? (() => {}),
    onWaypointsMoved: onLaneEditWaypointsMoved ?? (() => {}),
    onWaypointInserted: onLaneEditWaypointInserted,
    onWaypointRemoved: onLaneEditWaypointRemoved,
    onUndo: onLaneEditUndo,
    onRedo: onLaneEditRedo,
    onDeleteSelected: onLaneEditDeleteSelected,
  });

  // Speed breakpoint placer — click lane to place speed segment boundaries
  useSpeedPointPlacer({
    map,
    enabled: speedEditMode,
    waypoints: speedEditWaypoints,
    speedSegments: speedEditSegments,
    onBreakpointAdded: onSpeedBreakpointAdded,
  });

  // Persistent overlays for non-active lane segments that remain visible unless hidden via eye toggle.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    const clearPersistent = () => {
      const prev = persistentPolylinesRef.current;
      prev.forEach((entry) => {
        entry?.border?.setMap?.(null);
        entry?.main?.setMap?.(null);
        if (entry?.zoomListener && g.event?.removeListener) g.event.removeListener(entry.zoomListener);
      });
      persistentPolylinesRef.current = [];
    };

    clearPersistent();
    const overlays = Array.isArray(persistentPathOverlays) ? persistentPathOverlays : [];
    if (overlays.length === 0) return clearPersistent;

    const created = [];
    overlays.forEach((entry) => {
      const coords = entry?.pathData?.geometry?.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return;
      const path = coords
        .map((c) => {
          const lng = Array.isArray(c) ? c[0] : c?.lng;
          const lat = Array.isArray(c) ? c[1] : c?.lat;
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return { lat, lng };
        })
        .filter(Boolean);
      if (path.length < 2) return;

      const zoom = map.getZoom() ?? 12;
      const { main: mainW, border: borderW } = getStrokeWeightsForZoom(zoom);
      const border = new g.Polyline({
        path,
        strokeColor: ROUTE_POLYLINE_BORDER.strokeColor,
        strokeWeight: borderW,
        strokeOpacity: ROUTE_POLYLINE_BORDER.strokeOpacity,
        map,
        zIndex: 200,
        clickable: false,
      });
      const main = new g.Polyline({
        path,
        strokeColor: entry?.color || ROUTE_POLYLINE_OPTIONS.strokeColor,
        strokeWeight: mainW,
        strokeOpacity: ROUTE_POLYLINE_OPTIONS.strokeOpacity,
        map,
        zIndex: 201,
        clickable: false,
      });
      const updateWeights = () => {
        const z = map.getZoom() ?? 12;
        const { main: mw, border: bw } = getStrokeWeightsForZoom(z);
        border.setOptions({ strokeWeight: bw });
        main.setOptions({ strokeWeight: mw });
      };
      const zoomListener = g.event.addListener(map, 'zoom_changed', updateWeights);
      created.push({ border, main, zoomListener });
    });
    persistentPolylinesRef.current = created;

    return clearPersistent;
  }, [map, persistentPathOverlays]);

  // Highlight polyline: draw selected segment on top of main path when highlightPathData is set
  // Uses same style as main path (ROUTE_POLYLINE_OPTIONS / ROUTE_POLYLINE_BORDER) with segment strokeColor only
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const prev = highlightPolylineRef.current;
    if (prev) {
      const polylines = prev.border != null ? [prev.border, prev.main] : [prev];
      polylines.forEach((p) => p && p.setMap && p.setMap(null));
      if (prev.zoomListener && window.google?.maps?.event?.removeListener) {
        window.google.maps.event.removeListener(prev.zoomListener);
      }
      highlightPolylineRef.current = null;
    }
    const coords = highlightPathData?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    const path = coords.map((c) => {
      const lng = Array.isArray(c) ? c[0] : c.lng;
      const lat = Array.isArray(c) ? c[1] : c.lat;
      return { lat, lng };
    });
    const zoom = map.getZoom() ?? 12;
    const { main: mainW, border: borderW } = getStrokeWeightsForZoom(zoom);
    const border = new window.google.maps.Polyline({
      path,
      strokeColor: ROUTE_POLYLINE_BORDER.strokeColor,
      strokeWeight: borderW,
      strokeOpacity: ROUTE_POLYLINE_BORDER.strokeOpacity,
      map,
      zIndex: 240,
      clickable: false,
    });
    const main = new window.google.maps.Polyline({
      path,
      strokeColor: highlightColor,
      strokeWeight: mainW,
      strokeOpacity: ROUTE_POLYLINE_OPTIONS.strokeOpacity,
      map,
      zIndex: 241,
      clickable: false,
    });
    const updateWeights = () => {
      const z = map.getZoom() ?? 12;
      const { main: mw, border: bw } = getStrokeWeightsForZoom(z);
      border.setOptions({ strokeWeight: bw });
      main.setOptions({ strokeWeight: mw });
    };
    const zoomListener = window.google.maps.event.addListener(map, 'zoom_changed', updateWeights);
    highlightPolylineRef.current = { border, main, zoomListener };
    return () => {
      const curr = highlightPolylineRef.current;
      if (curr) {
        if (curr.border) curr.border.setMap(null);
        if (curr.main) curr.main.setMap(null);
        if (curr.zoomListener && window.google?.maps?.event?.removeListener) {
          window.google.maps.event.removeListener(curr.zoomListener);
        }
        highlightPolylineRef.current = null;
      }
    };
  }, [map, highlightPathData, highlightColor]);

  // Background route: keep the primary routed path visible behind active edits as read-only context.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const prev = fullPathPolylineRef.current;
    if (prev) {
      [prev.border, prev.main].forEach((p) => p && p.setMap && p.setMap(null));
      if (prev.zoomListener && window.google?.maps?.event?.removeListener) {
        window.google.maps.event.removeListener(prev.zoomListener);
      }
      fullPathPolylineRef.current = null;
    }
    const isFullPathVisible = segmentVisibility[0] !== false;
    if (!isFullPathVisible) return;
    const coords = fullPathPathData?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    const path = coords.map((c) => {
      const lng = Array.isArray(c) ? c[0] : c.lng;
      const lat = Array.isArray(c) ? c[1] : c.lat;
      return { lat, lng };
    });
    const zoom = map.getZoom() ?? 12;
    const { main: mainW, border: borderW } = getStrokeWeightsForZoom(zoom);
    const border = new window.google.maps.Polyline({
      path,
      strokeColor: ROUTE_POLYLINE_BORDER.strokeColor,
      strokeWeight: borderW,
      strokeOpacity: ROUTE_POLYLINE_BORDER.strokeOpacity,
      map,
      zIndex: 180,
      clickable: false,
    });
    const main = new window.google.maps.Polyline({
      path,
      strokeColor: fullPathStrokeColor,
      strokeWeight: mainW,
      strokeOpacity: ROUTE_POLYLINE_OPTIONS.strokeOpacity,
      map,
      zIndex: 181,
      clickable: false,
    });
    const updateWeights = () => {
      const z = map.getZoom() ?? 12;
      const { main: mw, border: bw } = getStrokeWeightsForZoom(z);
      border.setOptions({ strokeWeight: bw });
      main.setOptions({ strokeWeight: mw });
    };
    const zoomListener = window.google.maps.event.addListener(map, 'zoom_changed', updateWeights);
    fullPathPolylineRef.current = { border, main, zoomListener };
    return () => {
      const curr = fullPathPolylineRef.current;
      if (curr) {
        if (curr.border) curr.border.setMap(null);
        if (curr.main) curr.main.setMap(null);
        if (curr.zoomListener && window.google?.maps?.event?.removeListener) {
          window.google.maps.event.removeListener(curr.zoomListener);
        }
        fullPathPolylineRef.current = null;
      }
    };
  }, [map, fullPathPathData, fullPathStrokeColor, segmentVisibility]);

  return null;
}

// catmullRomSpline moved to ./splineUtils.js

const SPLINE_SAMPLES = 16;
const CURVED_EDIT_MARKER_SCALE = 6;
const CURVED_EDIT_MARKER_MIN_ZOOM = 10;
const CURVED_EDIT_MARKER_MIN_SCALE = 2;

function getCurvedEditMarkerScale(zoom) {
  const safeZoom = Number.isFinite(zoom) ? zoom : CURVED_EDIT_MARKER_MIN_ZOOM;
  if (safeZoom >= CURVED_EDIT_MARKER_MIN_ZOOM) return CURVED_EDIT_MARKER_SCALE;
  const scaled = CURVED_EDIT_MARKER_SCALE * Math.pow(2, safeZoom - CURVED_EDIT_MARKER_MIN_ZOOM);
  return Math.max(CURVED_EDIT_MARKER_MIN_SCALE, scaled);
}

function getCurvedEditMarkerIcon(g, zoom, scaleOffset = 0) {
  return {
    path: g.SymbolPath.CIRCLE,
    scale: getCurvedEditMarkerScale(zoom) + scaleOffset,
    fillColor: '#2563eb',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 2,
  };
}

/**
 * Find closest point on a path (array of {lat,lng}) to a given point.
 * Returns { point: {lat,lng}, pathIndex }.
 */
function closestPointOnSplinePath(path, lat, lng) {
  if (!path?.length) return null;
  let bestDist2 = Infinity;
  let bestPoint = null;
  let bestIndex = 0;
  for (let i = 0; i < path.length; i++) {
    const p = path[i];
    const d2 = (p.lat - lat) ** 2 + (p.lng - lng) ** 2;
    if (d2 < bestDist2) {
      bestDist2 = d2;
      bestPoint = p;
      bestIndex = i;
    }
  }
  if (bestPoint == null) return null;
  return { point: bestPoint, pathIndex: bestIndex };
}

/** Given path index in a spline with numSamplesPerSegment per segment, return control point segment index (0..n-2). */
function pathIndexToSegmentIndex(pathIndex, numSamplesPerSegment) {
  return Math.max(0, Math.floor(pathIndex / numSamplesPerSegment));
}

/** Distance between two points (approx, for resampling). */
function dist(a, b) {
  return Math.sqrt((a.lat - b.lat) ** 2 + (a.lng - b.lng) ** 2);
}

function getSegmentLabelPosition(a, b) {
  const midLat = (a.lat + b.lat) / 2;
  const midLng = (a.lng + b.lng) / 2;
  return { lat: midLat, lng: midLng };
}

function getSegmentLabelPlacement(a, b) {
  const midLat = (a.lat + b.lat) / 2;
  const cosLat = Math.max(1e-6, Math.cos((midLat * Math.PI) / 180));
  const vx = (b.lng - a.lng) * cosLat;
  const vy = b.lat - a.lat;
  if (Math.abs(vx) <= 1e-12 && Math.abs(vy) <= 1e-12) return { angleDeg: 0, offsetSign: -1 };
  // Screen-space adjustment: north(+lat) is visually up, so negate angle.
  let deg = -((Math.atan2(vy, vx) * 180) / Math.PI);
  let offsetSign = -1;
  // Keep text upright while aligned with the segment.
  if (deg > 90) {
    deg -= 180;
    offsetSign = 1;
  }
  if (deg < -90) {
    deg += 180;
    offsetSign = 1;
  }
  return { angleDeg: deg, offsetSign };
}

const MAX_LABEL_TO_SEGMENT_RATIO = 0.8; // hide when label width exceeds 80% of its segment

function createRotatedSegmentLabelOverlay(g, map, position, text, angleDeg, offsetPx = -10, segmentStart = null, segmentEnd = null) {
  if (!g?.OverlayView) return null;
  const overlay = new g.OverlayView();
  let div = null;
  let cachedLabelWidthPx = 0; // avoid re-measuring when hidden (display:none => offsetWidth is 0 and causes blink)
  overlay.onAdd = function onAdd() {
    div = document.createElement('div');
    div.textContent = text;
    div.style.position = 'absolute';
    div.style.pointerEvents = 'none';
    div.style.whiteSpace = 'nowrap';
    div.style.color = '#ffffff';
    div.style.fontSize = '12.5px';
    div.style.fontWeight = '800';
    div.style.fontFamily = '\'Letterboard Lite\', \'Arial Narrow\', Arial, sans-serif';
    div.style.letterSpacing = '0.04em';
    div.style.webkitTextStroke = '3.8px rgba(255, 0, 0, 0)';
    // Multi-direction shadow for a black halo around the text (not just bottom-right)
    const shadowR = '0.09em';
    const shadowBlur = '0.00em';
    div.style.textShadow = [
      `${shadowR} 0 ${shadowBlur} rgb(0,0,0)`,
      `-${shadowR} 0 ${shadowBlur} rgb(0,0,0)`,
      `0 ${shadowR} ${shadowBlur} rgb(0,0,0)`,
      `0 -${shadowR} ${shadowBlur} rgb(0,0,0)`,
      `${shadowR} ${shadowR} ${shadowBlur} rgb(0,0,0)`,
      `-${shadowR} -${shadowR} ${shadowBlur} rgb(0,0,0)`,
      `${shadowR} -${shadowR} ${shadowBlur} rgb(0,0,0)`,
      `-${shadowR} ${shadowR} ${shadowBlur} rgb(0,0,0)`,
    ].join(', ');
    div.style.background = 'rgba(255, 255, 255, 0)';
    div.style.borderRadius = '4px';
    div.style.padding = '1px 4px';
    this.getPanes()?.floatPane?.appendChild(div);
  };
  overlay.draw = function draw() {
    if (!div) return;
    const projection = this.getProjection();
    if (!projection) return;
    const px = projection.fromLatLngToDivPixel(new g.LatLng(position.lat, position.lng));
    if (!px) return;
    if (segmentStart && segmentEnd) {
      const aPx = projection.fromLatLngToDivPixel(new g.LatLng(segmentStart.lat, segmentStart.lng));
      const bPx = projection.fromLatLngToDivPixel(new g.LatLng(segmentEnd.lat, segmentEnd.lng));
      if (aPx && bPx) {
        const segLenPx = Math.hypot(bPx.x - aPx.x, bPx.y - aPx.y);
        const isVisible = div.style.display !== 'none';
        const labelWidthPx = isVisible
          ? (div.offsetWidth || div.getBoundingClientRect().width || 0)
          : cachedLabelWidthPx;
        if (isVisible && labelWidthPx > 0) cachedLabelWidthPx = labelWidthPx;
        if (Number.isFinite(segLenPx) && segLenPx > 0 && labelWidthPx > segLenPx * MAX_LABEL_TO_SEGMENT_RATIO) {
          div.style.display = 'none';
          return;
        }
      }
    }
    div.style.display = 'block';
    div.style.left = `${px.x}px`;
    div.style.top = `${px.y}px`;
    div.style.transform = `translate(-50%, -50%) rotate(${angleDeg}deg) translateY(${offsetPx}px)`;
    div.style.transformOrigin = 'center center';
  };
  overlay.onRemove = function onRemove() {
    if (div?.parentNode) div.parentNode.removeChild(div);
    div = null;
  };
  overlay.setMap(map);
  return overlay;
}

function formatDistance(meters = 0) {
  if (!Number.isFinite(meters) || meters <= 0) return '0 ft';
  const feet = meters * METERS_TO_FEET;
  return `${feet.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ft`;
}

/**
 * Resample a path to n points at equal arc-length distance along the path.
 * @param path - Array of { lat, lng }
 * @param n - Number of points (2 or more)
 * @returns Array of n points { lat, lng }
 */
function resamplePathToNPoints(path, n) {
  if (!path?.length || n < 2) return path?.length ? [path[0], path[path.length - 1]].slice(0, Math.max(1, n)) : [];
  const cumulative = [0];
  for (let i = 1; i < path.length; i++) {
    cumulative[i] = cumulative[i - 1] + dist(path[i - 1], path[i]);
  }
  const total = cumulative[path.length - 1];
  if (total <= 0) return [path[0], path[path.length - 1]];
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = (i / (n - 1)) * total;
    let idx = 0;
    while (idx < cumulative.length - 1 && cumulative[idx + 1] < d) idx++;
    const t = idx < cumulative.length - 1 && cumulative[idx + 1] > cumulative[idx]
      ? (d - cumulative[idx]) / (cumulative[idx + 1] - cumulative[idx])
      : 0;
    const a = path[idx];
    const b = path[Math.min(idx + 1, path.length - 1)];
    out.push({
      lat: a.lat + t * (b.lat - a.lat),
      lng: a.lng + t * (b.lng - a.lng),
    });
  }
  return out;
}

/** Resample a curve (control points) to n points at equal distance along the spline. */
function resampleCurveToNPoints(controlPoints, n) {
  if (!controlPoints?.length || n < 2) return controlPoints?.length ? [...controlPoints] : [];
  const path = catmullRomSpline(controlPoints, 32);
  return resamplePathToNPoints(path, n);
}

/** Extract coordinates from a Google Maps overlay for serialization */
function getOverlayCoordinates(overlay, type) {
  const toLatLng = (p) => {
    if (!p) return null;
    const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
    const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
    return lat != null && lng != null ? { lat, lng } : null;
  };
  if (type === 'marker' && overlay.getPosition) {
    const p = overlay.getPosition();
    return p ? [toLatLng(p)] : [];
  }
  if ((type === 'polyline' || type === 'polygon') && overlay.getPath) {
    const path = overlay.getPath();
    if (!path) return [];
    const arr = [];
    for (let i = 0; i < path.getLength(); i++) arr.push(toLatLng(path.getAt(i)));
    return arr;
  }
  if (type === 'circle' && overlay.getCenter && overlay.getRadius) {
    const c = overlay.getCenter();
    return [{ ...toLatLng(c), radiusMeters: overlay.getRadius() }];
  }
  if (type === 'rectangle' && overlay.getBounds) {
    const b = overlay.getBounds();
    if (!b) return [];
    const ne = b.getNorthEast();
    const sw = b.getSouthWest();
    return [toLatLng(sw), toLatLng(ne)];
  }
  return [];
}

/**
 * Drawing layer for Intersection mode: markers, polylines, curved lines, polygons, circles, rectangles.
 * Uses Google Maps Drawing Library; curved line is custom (click points → spline).
 */
function MapDrawingLayer({
  pathsPanelMode = 'path-creator',
  drawingMode = null,
  onOverlayComplete,
  onIntersectionOverlayUpdate,
  onIntersectionOverlayRemove = () => {},
  intersectionOverlays = [],
  curvePoints = [],
  onCurvedLinePointsChange,
  measurePoints = [],
  onMeasurePointsChange,
  curvedLineEditPointCount = 5,
  intersections = [],
  activeIntersectionId = null,
  selectedTargetId = null,
  onAddIntersection,
  onUpdateIntersection,
  onSetActiveIntersection,
  pendingIsdDraft = null,
  onCancelPendingIsdDraft,
  oppositePointPickActive = false,
  onOppositeBlockPicked,
  onCancelOppositePointPick,
  oppositeBlockDraft = null,
  onUpdateOppositeBlockDraft,
}) {
  const map = useGoogleMap();
  const isdOverlaysRef = useIsdBlockOverlays({
    map,
    intersections,
    activeIntersectionId,
    selectedTargetId,
    pathsPanelMode,
    onSetActiveIntersection,
  });
  useIsdBlockDrawing({
    map,
    pathsPanelMode,
    pendingIsdDraft,
    onCancelPendingIsdDraft,
    overlaysRef: isdOverlaysRef,
    activeIntersectionId,
    onAddIntersection,
    onUpdateIntersection,
    onSetActiveIntersection,
  });
  useIsdOppositePointPick({
    map,
    pathsPanelMode,
    oppositePointPickActive,
    onOppositeBlockPicked,
    onCancelOppositePointPick,
  });
  useIsdOppositeBlockOverlays({
    map,
    pathsPanelMode,
    intersections,
    activeIntersectionId,
    selectedTargetId,
    oppositeBlockDraft,
    onUpdateIntersection,
    onUpdateOppositeBlockDraft,
  });
  const drawingManagerRef = useRef(null);
  const drawingListenerRef = useRef(null);
  const overlayRefsRef = useRef([]);
  const curvedOverlaysRef = useRef({}); // { [overlayId]: { polyline, markers, dragListeners } }
  const previewPolylineRef = useRef(null);
  const curveFirstPointMarkerRef = useRef(null);
  const curvedLineClickListenerRef = useRef(null);
  const measureClickListenerRef = useRef(null);
  const measurePolylineRef = useRef(null);
  const measureMarkersRef = useRef([]);
  const measureMarkerDragListenersRef = useRef([]);
  const measureSegmentLabelsRef = useRef([]);
  const curvePointsRef = useRef(curvePoints);
  const measurePointsRef = useRef(measurePoints);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [polylinePoints, setPolylinePoints] = useState([]);
  const polylinePointsRef = useRef([]);
  const straightLineFirstPointMarkerRef = useRef(null);
  const polylineClickListenerRef = useRef(null);
  const onOverlayCompleteRef = useRef(onOverlayComplete);
  const onIntersectionOverlayUpdateRef = useRef(onIntersectionOverlayUpdate);

  const isCurvedMode = drawingMode === 'polyline-curved';
  const isMeasureMode = drawingMode === 'measure-distance';
  const isPolylineMode = drawingMode === 'polyline';
  const dmMode = (isCurvedMode || isMeasureMode || isPolylineMode) ? null : drawingMode;

  curvePointsRef.current = curvePoints;
  measurePointsRef.current = measurePoints;
  polylinePointsRef.current = polylinePoints;
  onOverlayCompleteRef.current = onOverlayComplete;
  onIntersectionOverlayUpdateRef.current = onIntersectionOverlayUpdate;

  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    const g = window.google.maps;
    const updateZoom = () => {
      const z = map.getZoom();
      if (Number.isFinite(z)) setMapZoom(z);
    };
    updateZoom();
    const zoomListener = g.event.addListener(map, 'zoom_changed', updateZoom);
    return () => {
      if (zoomListener && g.event?.removeListener) g.event.removeListener(zoomListener);
    };
  }, [map]);

  useEffect(() => {
    if (pathsPanelMode !== 'intersection' || !map || !window.google?.maps?.importLibrary) return;
    window.google.maps.importLibrary('drawing').then((drawingLib) => {
      const OverlayType = drawingLib.OverlayType;
      const drawingManager = new drawingLib.DrawingManager({
        drawingControl: false,
        drawingMode: dmMode === 'polyline' ? OverlayType.POLYLINE
          : dmMode === 'polygon' ? OverlayType.POLYGON
            : dmMode === 'circle' ? OverlayType.CIRCLE
              : dmMode === 'rectangle' ? OverlayType.RECTANGLE
                : null,
        polylineOptions: { strokeWeight: 4, strokeColor: '#2563eb', editable: true },
        polygonOptions: { fillOpacity: 0.3, strokeWeight: 2, strokeColor: '#2563eb', editable: true },
        circleOptions: { fillOpacity: 0.2, strokeWeight: 2, strokeColor: '#2563eb', editable: true },
        rectangleOptions: { fillOpacity: 0.2, strokeWeight: 2, strokeColor: '#2563eb', editable: true },
      });
      drawingManager.setMap(map);
      drawingManagerRef.current = drawingManager;

      const g = window.google.maps;
      const listener = g.event.addListener(drawingManager, 'overlaycomplete', (event) => {
        const overlay = event.overlay;
        const type = event.type;
        const coordinates = getOverlayCoordinates(overlay, type);
        const id = `draw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const entry = { overlay, id };
        if (type === 'polyline' || type === 'polygon') {
          const path = overlay.getPath?.();
          if (path) {
            const syncPath = () => {
              onIntersectionOverlayUpdate?.(id, { coordinates: getOverlayCoordinates(overlay, type) });
            };
            entry.pathListeners = [
              g.event.addListener(path, 'set_at', syncPath),
              g.event.addListener(path, 'insert_at', syncPath),
              g.event.addListener(path, 'remove_at', syncPath),
            ];
          }
        }
        overlayRefsRef.current.push(entry);
        onOverlayComplete?.({ id, type, coordinates });
      });
      drawingListenerRef.current = listener;
    }).catch((err) => console.warn('[MapDrawingLayer] Drawing library failed:', err));

    return () => {
      if (drawingListenerRef.current && window.google?.maps?.event?.removeListener) {
        window.google.maps.event.removeListener(drawingListenerRef.current);
        drawingListenerRef.current = null;
      }
      const dm = drawingManagerRef.current;
      if (dm) {
        dm.setMap(null);
        drawingManagerRef.current = null;
      }
    };
  }, [pathsPanelMode, map, dmMode, onOverlayComplete, onIntersectionOverlayRemove, onIntersectionOverlayUpdate]);

  // Clear in-progress polyline when leaving polyline drawing mode
  useEffect(() => {
    if (drawingMode !== 'polyline') setPolylinePoints([]);
  }, [drawingMode]);

  // When leaving intersection mode (e.g. path-creator or maps), remove DrawingManager so map clicks work for waypoints
  useEffect(() => {
    if (pathsPanelMode === 'intersection') return;
    const dm = drawingManagerRef.current;
    if (dm) {
      dm.setMap(null);
      drawingManagerRef.current = null;
    }
  }, [pathsPanelMode]);

  // Sync drawing mode when it changes (DrawingManager already created)
  useEffect(() => {
    const dm = drawingManagerRef.current;
    if (!dm || pathsPanelMode !== 'intersection' || !window.google?.maps?.importLibrary) return;
    window.google.maps.importLibrary('drawing').then((drawingLib) => {
      const OverlayType = drawingLib.OverlayType;
      const mode = dmMode === 'polyline' ? OverlayType.POLYLINE
        : dmMode === 'polygon' ? OverlayType.POLYGON
          : dmMode === 'circle' ? OverlayType.CIRCLE
            : dmMode === 'rectangle' ? OverlayType.RECTANGLE
              : null;
      dm.setDrawingMode(mode);
    });
  }, [pathsPanelMode, dmMode]);

  // Straight line (polyline): map click adds points; right-click / Esc / Enter / double-click completes (cuts) the line
  useEffect(() => {
    if (pathsPanelMode !== 'intersection' || !map || !isPolylineMode || !window.google?.maps) return;
    const g = window.google.maps;
    const clickListener = g.event.addListener(map, 'click', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) setPolylinePoints((prev) => [...prev, { lat, lng }]);
    });
    polylineClickListenerRef.current = clickListener;
    return () => {
      if (polylineClickListenerRef.current && g.event?.removeListener) {
        g.event.removeListener(polylineClickListenerRef.current);
        polylineClickListenerRef.current = null;
      }
    };
  }, [pathsPanelMode, map, isPolylineMode]);

  // Straight line: show first-click marker only while waiting for 2nd point (remove when 2nd is clicked to avoid double marker with polyline edit handle)
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    if (straightLineFirstPointMarkerRef.current) {
      straightLineFirstPointMarkerRef.current.setMap(null);
      straightLineFirstPointMarkerRef.current = null;
    }
    if (!isPolylineMode || polylinePoints.length !== 1) return () => {};
    const marker = new g.Marker({
      position: polylinePoints[0],
      map,
      clickable: false,
      draggable: false,
      zIndex: 9999,
      icon: getMeasurePointMarkerIcon(g),
    });
    straightLineFirstPointMarkerRef.current = marker;
    return () => {
      if (straightLineFirstPointMarkerRef.current) {
        straightLineFirstPointMarkerRef.current.setMap(null);
        straightLineFirstPointMarkerRef.current = null;
      }
    };
  }, [map, isPolylineMode, polylinePoints]);

  // Straight line: exactly 2 points per line; 3rd click starts a new line
  useEffect(() => {
    if (!map || !window.google?.maps || !isPolylineMode || polylinePoints.length !== 2) return () => {};
    const g = window.google.maps;
    const coordinates = polylinePoints.map((p) => ({ lat: p.lat, lng: p.lng }));
    const id = `draw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const polyline = new g.Polyline({
      path: coordinates,
      strokeWeight: 4,
      strokeColor: '#2563eb',
      map,
      editable: true,
    });
    const path = polyline.getPath();
    const syncPath = () => {
      onIntersectionOverlayUpdateRef.current?.(id, { coordinates: getOverlayCoordinates(polyline, 'polyline') });
    };
    const pathListeners = path
      ? [
          g.event.addListener(path, 'set_at', syncPath),
          g.event.addListener(path, 'insert_at', syncPath),
          g.event.addListener(path, 'remove_at', syncPath),
        ]
      : [];
    overlayRefsRef.current.push({ overlay: polyline, id, pathListeners });
    onOverlayCompleteRef.current?.({ id, type: 'polyline', coordinates });
    setPolylinePoints([]); // reset so next click starts a new line
    return () => {};
  }, [map, isPolylineMode, polylinePoints]);

  // Curved line: map click adds points (ref so listener stays stable)
  useEffect(() => {
    if (pathsPanelMode !== 'intersection' || !map || !isCurvedMode || !window.google?.maps) return;
    const g = window.google.maps;
    const clickListener = g.event.addListener(map, 'click', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) {
        const prev = curvePointsRef.current;
        onCurvedLinePointsChange?.([...prev, { lat, lng }]);
      }
    });
    curvedLineClickListenerRef.current = clickListener;
    return () => {
      if (curvedLineClickListenerRef.current && g.event?.removeListener) {
        g.event.removeListener(curvedLineClickListenerRef.current);
        curvedLineClickListenerRef.current = null;
      }
    };
  }, [pathsPanelMode, map, isCurvedMode, onCurvedLinePointsChange]);

  // Curved line first-click marker: show first point immediately, then hide when second point is added.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    if (curveFirstPointMarkerRef.current) {
      curveFirstPointMarkerRef.current.setMap(null);
      curveFirstPointMarkerRef.current = null;
    }
    if (!isCurvedMode || curvePoints.length !== 1) return;
    const marker = new g.Marker({
      position: curvePoints[0],
      map,
      clickable: false,
      draggable: false,
      zIndex: 9999,
      icon: getMeasurePointMarkerIcon(g),
    });
    curveFirstPointMarkerRef.current = marker;
    return () => {
      if (curveFirstPointMarkerRef.current) {
        curveFirstPointMarkerRef.current.setMap(null);
        curveFirstPointMarkerRef.current = null;
      }
    };
  }, [map, isCurvedMode, curvePoints, mapZoom]);

  // Distance measure: map click adds points to a temporary measured path.
  useEffect(() => {
    if (pathsPanelMode !== 'intersection' || !map || !isMeasureMode || !window.google?.maps) return;
    const g = window.google.maps;
    const clickListener = g.event.addListener(map, 'click', (e) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) {
        const prev = measurePointsRef.current;
        onMeasurePointsChange?.([...prev, { lat, lng }]);
      }
    });
    measureClickListenerRef.current = clickListener;
    return () => {
      if (measureClickListenerRef.current && g.event?.removeListener) {
        g.event.removeListener(measureClickListenerRef.current);
        measureClickListenerRef.current = null;
      }
    };
  }, [pathsPanelMode, map, isMeasureMode, onMeasurePointsChange]);

  // Render current measuring path and per-segment distance labels.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    const g = window.google.maps;
    if (measurePolylineRef.current) {
      measurePolylineRef.current.setMap(null);
      measurePolylineRef.current = null;
    }
    measureMarkerDragListenersRef.current.forEach((l) => l && g?.event?.removeListener(l));
    measureMarkerDragListenersRef.current = [];
    measureMarkersRef.current.forEach((m) => m.setMap(null));
    measureMarkersRef.current = [];
    measureSegmentLabelsRef.current.forEach((m) => m.setMap(null));
    measureSegmentLabelsRef.current = [];
    if (!isMeasureMode || measurePoints.length < 1) return;
    const polyline = new g.Polyline({
      path: measurePoints,
      strokeColor: '#FAB005',
      strokeWeight: 3,
      strokeOpacity: 0.95,
      geodesic: true,
      map,
      clickable: false,
    });
    measurePolylineRef.current = polyline;
    measureMarkersRef.current = measurePoints.map((pt, idx) => {
      const marker = new g.Marker({
        position: pt,
        map,
        draggable: true,
        icon: getMeasurePointMarkerIcon(g),
      });
      const dragListener = g.event.addListener(marker, 'dragend', () => {
        const pos = marker.getPosition();
        if (!pos) return;
        const next = [...measurePointsRef.current];
        if (idx >= 0 && idx < next.length) {
          next[idx] = { lat: pos.lat(), lng: pos.lng() };
          onMeasurePointsChange?.(next);
        }
      });
      measureMarkerDragListenersRef.current.push(dragListener);
      return marker;
    });

    if (measurePoints.length >= 2) {
      measureSegmentLabelsRef.current = measurePoints.slice(1).map((b, idx) => {
        const a = measurePoints[idx];
        const segMeters = haversineMeters(a.lat, a.lng, b.lat, b.lng);
        const position = getSegmentLabelPosition(a, b);
        const { angleDeg, offsetSign } = getSegmentLabelPlacement(a, b);
        const overlay = createRotatedSegmentLabelOverlay(g, map, position, formatDistance(segMeters), angleDeg, offsetSign * 10, a, b);
        if (overlay) return overlay;
        return new g.Marker({
          position,
          map,
          clickable: false,
          zIndex: 9999,
          icon: { path: g.SymbolPath.CIRCLE, scale: 0, fillOpacity: 0, strokeOpacity: 0 },
          label: {
            text: formatDistance(segMeters),
            color: '#14532d',
            fontSize: '12px',
            fontWeight: '100',
            fontFamily: 'Letterboard Lite',
          },
        });
      });
    }

    return () => {
      if (measurePolylineRef.current) {
        measurePolylineRef.current.setMap(null);
        measurePolylineRef.current = null;
      }
      measureMarkerDragListenersRef.current.forEach((l) => l && g?.event?.removeListener(l));
      measureMarkerDragListenersRef.current = [];
      measureMarkersRef.current.forEach((m) => m.setMap(null));
      measureMarkersRef.current = [];
      measureSegmentLabelsRef.current.forEach((m) => m.setMap(null));
      measureSegmentLabelsRef.current = [];
    };
  }, [map, isMeasureMode, measurePoints, onMeasurePointsChange]);

  // Preview polyline for curved line (spline through curvePoints)
  useEffect(() => {
    if (!map || !window.google?.maps || !isCurvedMode) return;
    if (previewPolylineRef.current) {
      previewPolylineRef.current.setMap(null);
      previewPolylineRef.current = null;
    }
    if (curvePoints.length < 2) return;
    const path = catmullRomSpline(curvePoints, 12);
    const polyline = new window.google.maps.Polyline({
      path,
      strokeColor: '#2563eb',
      strokeWeight: 4,
      strokeOpacity: 0.8,
      map,
    });
    previewPolylineRef.current = polyline;
    return () => {
      if (previewPolylineRef.current) {
        previewPolylineRef.current.setMap(null);
        previewPolylineRef.current = null;
      }
    };
  }, [map, isCurvedMode, curvePoints]);

  // Auto-finish curved line on second click: create editable control points immediately.
  useEffect(() => {
    if (!isCurvedMode || curvePoints.length < 2) return;
    if (previewPolylineRef.current) {
      previewPolylineRef.current.setMap(null);
      previewPolylineRef.current = null;
    }
    const n = Math.max(3, Math.min(10, curvedLineEditPointCount));
    const controlPoints = resampleCurveToNPoints(curvePoints, n);
    const id = `draw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    onOverlayComplete?.({ id, type: 'polyline-curved', controlPoints });
    onCurvedLinePointsChange?.([]);
  }, [isCurvedMode, curvePoints, curvedLineEditPointCount, onOverlayComplete, onCurvedLinePointsChange]);

  // Render curved overlays from state: one polyline (spline, clickable) + draggable markers per control point
  useEffect(() => {
    if (!map || !window.google?.maps || pathsPanelMode !== 'intersection') return;
    const g = window.google.maps;
    const curvedEditMarkerIcon = getMeasurePointMarkerIcon(g);
    const curved = intersectionOverlays.filter((o) => o.type === 'polyline-curved' && Array.isArray(o.controlPoints));
    const curvedIds = new Set(curved.map((o) => o.id));

    // Remove curved overlays that are no longer in state
    Object.keys(curvedOverlaysRef.current).forEach((id) => {
      if (!curvedIds.has(id)) {
        const entry = curvedOverlaysRef.current[id];
        if (entry.polyline) entry.polyline.setMap(null);
        entry.markers?.forEach((m) => m.setMap(null));
        entry.dragListeners?.forEach((l) => l && g.event?.removeListener(l));
        delete curvedOverlaysRef.current[id];
      }
    });

    curved.forEach((overlay) => {
      const { id, controlPoints } = overlay;
      const path = catmullRomSpline(controlPoints, SPLINE_SAMPLES);
      let entry = curvedOverlaysRef.current[id];

      if (!entry) {
        const polyline = new g.Polyline({
          path,
          strokeColor: '#2563eb',
          strokeWeight: 4,
          strokeOpacity: 0.9,
          map,
          editable: false,
          clickable: false,
        });
        const markers = controlPoints.map((pt, idx) => {
          const m = new g.Marker({
            position: pt,
            map,
            draggable: true,
            icon: curvedEditMarkerIcon,
          });
          const dragListener = g.event.addListener(m, 'dragend', () => {
            const pos = m.getPosition();
            if (!pos) return;
            const lat = pos.lat();
            const lng = pos.lng();
            const next = [...(curvedOverlaysRef.current[id]?.controlPointsSnapshot ?? controlPoints)];
            if (idx >= 0 && idx < next.length) {
              next[idx] = { lat, lng };
              onIntersectionOverlayUpdate?.(id, { controlPoints: next });
            }
          });
          return { marker: m, dragListener };
        });
        entry = {
          polyline,
          markers: markers.map((x) => x.marker),
          dragListeners: markers.map((x) => x.dragListener),
          controlPointsSnapshot: controlPoints,
        };
        entry.controlPointsSnapshot = controlPoints;
        curvedOverlaysRef.current[id] = entry;
      } else {
        entry.polyline.setPath(path);
        entry.controlPointsSnapshot = controlPoints;
        const existingMarkers = entry.markers?.length ?? 0;
        if (existingMarkers !== controlPoints.length) {
          entry.markers?.forEach((m) => m.setMap(null));
          entry.dragListeners?.forEach((l) => l && g.event?.removeListener(l));
          const markers = controlPoints.map((pt, idx) => {
            const m = new g.Marker({
              position: pt,
              map,
              draggable: true,
              icon: curvedEditMarkerIcon,
            });
            const dragListener = g.event.addListener(m, 'dragend', () => {
              const pos = m.getPosition();
              if (!pos) return;
              const lat = pos.lat();
              const lng = pos.lng();
              const next = [...(curvedOverlaysRef.current[id]?.controlPointsSnapshot ?? controlPoints)];
              if (idx >= 0 && idx < next.length) {
                next[idx] = { lat, lng };
                onIntersectionOverlayUpdate?.(id, { controlPoints: next });
              }
            });
            return { marker: m, dragListener };
          });
          entry.markers = markers.map((x) => x.marker);
          entry.dragListeners = markers.map((x) => x.dragListener);
        } else {
          entry.markers?.forEach((m, i) => {
            if (controlPoints[i]) m.setPosition(controlPoints[i]);
            m.setIcon(curvedEditMarkerIcon);
          });
        }
      }
    });
  }, [map, pathsPanelMode, intersectionOverlays, onIntersectionOverlayUpdate, mapZoom]);

  // When curvedLineEditPointCount changes, resample all curved overlays to N points at equal distance
  const prevEditPointCountRef = useRef(curvedLineEditPointCount);
  useEffect(() => {
    const n = Math.max(3, Math.min(10, curvedLineEditPointCount));
    if (prevEditPointCountRef.current === n) return;
    prevEditPointCountRef.current = n;
    intersectionOverlays.forEach((overlay) => {
      if (overlay.type === 'polyline-curved' && Array.isArray(overlay.controlPoints) && overlay.controlPoints.length >= 2) {
        const resampled = resampleCurveToNPoints(overlay.controlPoints, n);
        onIntersectionOverlayUpdate?.(overlay.id, { controlPoints: resampled });
      }
    });
  }, [curvedLineEditPointCount, intersectionOverlays, onIntersectionOverlayUpdate]);

  // When a single overlay is removed from the list, remove only that overlay from the map
  useEffect(() => {
    const g = window.google?.maps;
    if (!g?.event?.removeListener) return;
    const currentIds = new Set(intersectionOverlays.map((o) => o.id));

    // Remove from overlayRefsRef any entry whose id is no longer in the list
    overlayRefsRef.current = overlayRefsRef.current.filter((entry) => {
      if (currentIds.has(entry.id)) return true;
      const ov = entry.overlay ?? entry;
      if (ov && typeof ov.setMap === 'function') ov.setMap(null);
      [entry.rightclickListener, entry.dragendListener, entry.dragstartListener, entry.mouseoverListener, entry.mouseoutListener].forEach((listener) => {
        if (listener) g.event.removeListener(listener);
      });
      entry.pathListeners?.forEach((listener) => {
        if (listener) g.event.removeListener(listener);
      });
      return false;
    });

    // Remove from curvedOverlaysRef any id no longer in the list
    Object.keys(curvedOverlaysRef.current).forEach((id) => {
      if (currentIds.has(id)) return;
      const entry = curvedOverlaysRef.current[id];
      if (entry.polyline) entry.polyline.setMap(null);
      entry.markers?.forEach((m) => m.setMap(null));
      entry.dragListeners?.forEach((l) => l && g.event.removeListener(l));
      delete curvedOverlaysRef.current[id];
    });
  }, [intersectionOverlays]);

  // When parent clears all overlays, remove all overlays from the map
  useEffect(() => {
    if (intersectionOverlays.length > 0) return;
    const gClear = window.google?.maps;
    overlayRefsRef.current.forEach((entry) => {
      const ov = entry.overlay ?? entry;
      if (ov && typeof ov.setMap === 'function') ov.setMap(null);
      [entry.rightclickListener, entry.dragendListener, entry.dragstartListener, entry.mouseoverListener, entry.mouseoutListener].forEach((listener) => {
        if (listener && gClear?.event?.removeListener) gClear.event.removeListener(listener);
      });
      entry.pathListeners?.forEach((listener) => {
        if (listener && gClear?.event?.removeListener) gClear.event.removeListener(listener);
      });
    });
    overlayRefsRef.current = [];
    const g = window.google?.maps;
    Object.keys(curvedOverlaysRef.current).forEach((id) => {
      const entry = curvedOverlaysRef.current[id];
      if (entry.polyline) entry.polyline.setMap(null);
      entry.markers?.forEach((m) => m.setMap(null));
      entry.dragListeners?.forEach((l) => l && g?.event?.removeListener(l));
    });
    curvedOverlaysRef.current = {};
  }, [intersectionOverlays.length]);

  return null;
}

/** Search input to pan the map to a location (address, city, country, etc.) */
function MapSearchBox({
  pathsPanelMode = 'path-creator',
  onPathsPanelModeChange,
  intersectionDrawingMode = null,
  measurePoints = [],
  onMeasurePointsChange,
}) {
  const map = useGoogleMap();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(''); // '' | 'loading' | 'error'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const activeSidebarBtn = pathsPanelMode;
  const setActiveSidebarBtn = onPathsPanelModeChange || (() => {});
  const isMeasureMode = pathsPanelMode === 'intersection' && intersectionDrawingMode === 'measure-distance';
  const measureDistanceMeters = getPolylineDistanceMeters(measurePoints);

  const handleSearch = useCallback(() => {
    if (!query.trim() || !map || !window.google?.maps?.importLibrary) return;
    setStatus('loading');
    window.google.maps.importLibrary('geocoding').then(({ Geocoder }) => {
      const geocoder = new Geocoder();
      geocoder.geocode({ address: query.trim() })
        .then((response) => {
          if (response.results?.[0]) {
            const loc = response.results[0].geometry?.location;
            if (loc) {
              map.panTo(loc);
              map.setZoom(Math.max(map.getZoom() || 12, 14));
              setStatus('');
            } else {
              setStatus('Location not found');
            }
          } else {
            setStatus('Location not found');
          }
        })
        .catch(() => setStatus('Search failed'))
        .finally(() => {
          setTimeout(() => setStatus(''), 3000);
        });
    }).catch(() => setStatus('Search failed'));
  }, [map, query]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSearch();
  }, [handleSearch]);

  const handleResetMeasure = useCallback((e) => {
    e.stopPropagation();
    onMeasurePointsChange?.([]);
  }, [onMeasurePointsChange]);

  if (!map) return null;
  return (
    <>
      <div
        className={`path-map-measure-popup ${isMeasureMode ? 'path-map-measure-popup-open' : ''}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="path-map-measure-popup-text">Total distance:</span>
        <span className="path-map-measure-popup-text-number">{formatDistance(measureDistanceMeters)}</span>
        <button
          type="button"
          className="path-map-measure-popup-reset"
          onClick={handleResetMeasure}
          title="Reset measurement"
          aria-label="Reset measurement"
          disabled={measurePoints.length === 0}
        >
          <i className="fas fa-rotate-left" aria-hidden />
        </button>
      </div>
      <div
        className="path-map-search-wrapper"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="path-map-search-input-wrap">
          <button
            type="button"
            className="path-map-search-hamburger"
            onClick={(e) => { e.stopPropagation(); setSidebarOpen((o) => !o); }}
            title="Open menu"
            aria-label="Open menu"
            aria-expanded={sidebarOpen}
          >
            <i className="fas fa-bars" aria-hidden />
          </button>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search address, city, or place…"
            className="path-map-search-box path-map-search-input"
            aria-label="Search location"
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={status === 'loading'}
            className="path-map-search-btn"
            title="Go to location"
            aria-label="Go to location"
          >
            <i className={`fas ${status === 'loading' ? 'fa-spinner fa-spin' : 'fa-search'}`} />
          </button>
          {status && status !== 'loading' && (
            <span className="path-map-search-status error">{status}</span>
          )}
        </div>
      </div>
      <div
        className={`path-map-left-sidebar ${sidebarOpen ? 'path-map-left-sidebar-open' : ''}`}
        role="dialog"
        aria-label="Map menu"
      >
        <button
          type="button"
          className="path-map-left-sidebar-close"
          onClick={() => setSidebarOpen((open) => !open)}
          title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          aria-expanded={sidebarOpen}
        >
          <i className={`fa-solid ${sidebarOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`} aria-hidden />
        </button>
        <div className="path-map-left-sidebar-content">
          <button
            type="button"
            className="path-map-sidebar-item"
            onClick={() => setActiveSidebarBtn('path-creator')}
            title="Path Creator"
            aria-label="Path Creator"
          >
            <span className={`path-map-sidebar-btn ${activeSidebarBtn === 'path-creator' ? 'active' : ''}`}>
              <i className="fa-solid fa-map-location-dot path-map-sidebar-btn-icon" aria-hidden />
            </span>
            <span className="path-map-sidebar-btn-caption" aria-hidden="true">Paths</span>
          </button>
          <button
            type="button"
            className="path-map-sidebar-item"
            onClick={() => setActiveSidebarBtn('intersection')}
            title="intersection"
            aria-label="intersection"
          >
            <span className={`path-map-sidebar-btn ${activeSidebarBtn === 'intersection' ? 'active' : ''}`}>
              <i className="fa-solid fa-traffic-light path-map-sidebar-btn-icon" aria-hidden />
            </span>
            <span className="path-map-sidebar-btn-caption" aria-hidden="true">intersection</span>
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Custom OverlayView that draws an image over lat/lng bounds using DOM (not GroundOverlay).
 * GroundOverlay can cull or unload tiles when zoomed/panned; this keeps every tile in the
 * overlay layer and repositions on each draw() so all tiles remain "live" as you zoom.
 * imageOverlays: Array<{ url: string, bounds: { south, west, north, east } }>
 */
function createImageBoundsOverlayView(g, bounds, imageUrl, opacity, brightness = 1, contrast = 1) {
  // bounds: { south, west, north, east } — numeric degrees
  class ImageBoundsOverlay extends g.OverlayView {
    constructor() {
      super();
      this.bounds_ = bounds;
      this.imageUrl_ = imageUrl;
      this.opacity_ = opacity;
      this.brightness_ = brightness;
      this.contrast_ = contrast;
      this.div_ = null;
    }
    onAdd() {
      const div = document.createElement('div');
      div.style.cssText =
        'position:absolute;border:none;pointer-events:none;overflow:hidden;box-sizing:border-box;z-index:1;';
      const img = document.createElement('img');
      img.src = this.imageUrl_;
      img.alt = '';
      img.draggable = false;
      img.style.cssText =
        'width:100%;height:100%;display:block;user-select:none;-webkit-user-drag:none;';
      img.style.opacity = String(this.opacity_);
      img.style.filter = `brightness(${this.brightness_}) contrast(${this.contrast_})`;
      div.appendChild(img);
      this.img_ = img;
      this.div_ = div;
      const panes = this.getPanes();
      // overlayLayer — same layer GroundOverlay uses; stays tied to map projection
      panes.overlayLayer.appendChild(div);
    }
    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div_) return;
      const b = this.bounds_;
      const sw = overlayProjection.fromLatLngToDivPixel(
        new g.LatLng(b.south, b.west)
      );
      const ne = overlayProjection.fromLatLngToDivPixel(
        new g.LatLng(b.north, b.east)
      );
      if (!sw || !ne) return;
      const div = this.div_;
      const x1 = Math.min(sw.x, ne.x);
      const x2 = Math.max(sw.x, ne.x);
      const y1 = Math.min(sw.y, ne.y);
      const y2 = Math.max(sw.y, ne.y);
      div.style.left = `${x1}px`;
      div.style.top = `${y1}px`;
      div.style.width = `${Math.max(0, x2 - x1)}px`;
      div.style.height = `${Math.max(0, y2 - y1)}px`;
    }
    /** Update opacity without removing the overlay (avoids re-fetching images). */
    setOverlayOpacity(opacity) {
      this.opacity_ = opacity;
      if (this.img_) {
        this.img_.style.opacity = String(opacity);
      }
    }
    setOverlayFilter(brightness, contrast) {
      this.brightness_ = brightness;
      this.contrast_ = contrast;
      if (this.img_) {
        this.img_.style.filter = `brightness(${brightness}) contrast(${contrast})`;
      }
    }
    onRemove() {
      if (this.div_?.parentNode) {
        this.div_.parentNode.removeChild(this.div_);
      }
      this.div_ = null;
      this.img_ = null;
    }
  }
  const overlay = new ImageBoundsOverlay();
  return overlay;
}

function isFiniteBounds(bounds) {
  return (
    !!bounds &&
    typeof bounds.south === 'number' &&
    typeof bounds.north === 'number' &&
    typeof bounds.west === 'number' &&
    typeof bounds.east === 'number'
  );
}

function isFiniteQuad(quad) {
  return (
    Array.isArray(quad) &&
    quad.length === 4 &&
    quad.every(
      (p) =>
        p &&
        typeof p.lat === 'number' &&
        Number.isFinite(p.lat) &&
        typeof p.lng === 'number' &&
        Number.isFinite(p.lng)
    )
  );
}

/**
 * Custom OverlayView for rotated/skewed ortho tiles.
 * quad order: [top-left, top-right, bottom-right, bottom-left]
 */
function createImageQuadOverlayView(g, quad, imageUrl, opacity, imageSize = {}, brightness = 1, contrast = 1) {
  class ImageQuadOverlay extends g.OverlayView {
    constructor() {
      super();
      this.quad_ = quad;
      this.imageUrl_ = imageUrl;
      this.opacity_ = opacity;
      this.brightness_ = brightness;
      this.contrast_ = contrast;
      this.div_ = null;
      this.img_ = null;
      this.imageWidth_ = Math.max(1, Number(imageSize.width) || 1);
      this.imageHeight_ = Math.max(1, Number(imageSize.height) || 1);
    }
    onAdd() {
      const div = document.createElement('div');
      div.style.cssText =
        'position:absolute;border:none;pointer-events:none;overflow:visible;box-sizing:border-box;transform-origin:0 0;z-index:1;';
      div.style.width = `${this.imageWidth_}px`;
      div.style.height = `${this.imageHeight_}px`;
      const img = document.createElement('img');
      img.src = this.imageUrl_;
      img.alt = '';
      img.draggable = false;
      img.style.cssText =
        'width:100%;height:100%;display:block;user-select:none;-webkit-user-drag:none;';
      img.style.opacity = String(this.opacity_);
      img.style.filter = `brightness(${this.brightness_}) contrast(${this.contrast_})`;
      div.appendChild(img);
      this.img_ = img;
      this.div_ = div;
      const panes = this.getPanes();
      panes.overlayLayer.appendChild(div);
    }
    draw() {
      const overlayProjection = this.getProjection();
      if (!overlayProjection || !this.div_ || !isFiniteQuad(this.quad_)) return;
      const px = this.quad_.map((p) =>
        overlayProjection.fromLatLngToDivPixel(new g.LatLng(p.lat, p.lng))
      );
      if (px.some((p) => !p)) return;

      const topLeft = px[0];
      const topRight = px[1];
      const bottomLeft = px[3];
      const w = this.imageWidth_;
      const h = this.imageHeight_;
      const a = (topRight.x - topLeft.x) / w;
      const b = (topRight.y - topLeft.y) / w;
      const c = (bottomLeft.x - topLeft.x) / h;
      const d = (bottomLeft.y - topLeft.y) / h;
      const e = topLeft.x;
      const f = topLeft.y;
      this.div_.style.transform = `matrix(${a}, ${b}, ${c}, ${d}, ${e}, ${f})`;
    }
    setOverlayOpacity(nextOpacity) {
      this.opacity_ = nextOpacity;
      if (this.img_) {
        this.img_.style.opacity = String(nextOpacity);
      }
    }
    setOverlayFilter(brightness, contrast) {
      this.brightness_ = brightness;
      this.contrast_ = contrast;
      if (this.img_) {
        this.img_.style.filter = `brightness(${brightness}) contrast(${contrast})`;
      }
    }
    onRemove() {
      if (this.div_?.parentNode) {
        this.div_.parentNode.removeChild(this.div_);
      }
      this.div_ = null;
      this.img_ = null;
    }
  }
  return new ImageQuadOverlay();
}

/**
 * Renders PNG overlays using custom OverlayView per tile (not GroundOverlay) so every
 * tile stays in the DOM and is repositioned on pan/zoom instead of being culled.
 * Preloads each image; when loaded, adds the overlay and calls onLoadProgress(loaded, total).
 */
// Ortho tiles are immutable persistent project assets, but a route can carry
// hundreds of them. Loading every PNG at once floods the ~6-connection-per-host
// browser limit and the shared backend proxy, which both slows the first paint
// and causes transient failures (Drive 404 / CloudFront blip / connection
// reset) that the old code dropped permanently. This loader instead:
//   - caps in-flight loads (MAX_CONCURRENT) and queues the rest;
//   - prioritises tiles inside the current map viewport so the visible area
//     paints in ~1s while off-screen tiles stream in behind it;
//   - retries failed tiles with exponential backoff (+ cache-bust) before
//     giving up, so transient blips self-heal instead of leaving holes.
const ORTHO_MAX_CONCURRENT = 6;
// One entry per retry; length = max retry attempts after the first failure.
const ORTHO_RETRY_DELAYS_MS = [600, 1800, 4500];

function ImageOverlays({
  imageOverlays = [],
  overlayOpacity = 0.9,
  overlayBrightness = 1,
  overlayContrast = 1,
  onLoadProgress,
  onFailedChange,
  retryNonce = 0,
}) {
  const map = useGoogleMap();
  // Map<name, { overlay, url, loading }> persists across deps changes so we
  // can diff-add/diff-remove instead of wiping every overlay on each prop
  // change. The previous implementation re-fetched all images on every
  // setOrthoTiles append during a streaming run, causing visible blinking
  // and dropping tiles whose loads were canceled mid-flight.
  const overlaysRef = useRef(new Map());
  // Map<name, url> of tiles that exhausted all retries — counted as settled so
  // the loader bar can close even when some tiles fail, and surfaced via
  // onFailedChange so the UI can offer a one-click retry of just these.
  const erroredRef = useRef(new Map());
  // Pending tiles waiting for a load slot (array of imageOverlay items).
  const queueRef = useRef([]);
  // Names with an in-flight Image() load.
  const inFlightRef = useRef(new Set());
  // name -> number of failed attempts so far (drives backoff + cache-bust).
  const attemptsRef = useRef(new Map());
  // Outstanding retry timers, cleared on unmount.
  const timersRef = useRef(new Set());
  // Current wanted items keyed by name — async callbacks read this (not the
  // captured `items` array) so a tile deleted mid-flight is detected.
  const itemsByNameRef = useRef(new Map());
  const totalRef = useRef(0);
  // Padded lat/lng viewport, refreshed on map 'idle'. null = load everything.
  const viewportRef = useRef(null);
  // Stable handle so the idle listener can re-pump the queue with the latest
  // closure (which captures the live google map) after the user pans/zooms.
  const pumpRef = useRef(null);
  const lastRetryNonceRef = useRef(retryNonce);
  const overlayOpacityRef = useRef(overlayOpacity);
  overlayOpacityRef.current = overlayOpacity;
  const overlayBrightnessRef = useRef(overlayBrightness);
  overlayBrightnessRef.current = overlayBrightness;
  const overlayContrastRef = useRef(overlayContrast);
  overlayContrastRef.current = overlayContrast;
  const onLoadProgressRef = useRef(onLoadProgress);
  onLoadProgressRef.current = onLoadProgress;
  const onFailedChangeRef = useRef(onFailedChange);
  onFailedChangeRef.current = onFailedChange;

  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const g = window.google.maps;
    const items = (Array.isArray(imageOverlays) ? imageOverlays : []).filter(
      (item) =>
        item?.name && item?.url && (isFiniteQuad(item?.quad) || isFiniteBounds(item?.bounds))
    );
    const wantedNames = new Set(items.map((i) => i.name));
    totalRef.current = items.length;
    itemsByNameRef.current = new Map(items.map((i) => [i.name, i]));

    const tileBBox = (item) => {
      if (isFiniteQuad(item?.quad)) {
        let s = Infinity, n = -Infinity, w = Infinity, e = -Infinity;
        for (const p of item.quad) {
          s = Math.min(s, p.lat); n = Math.max(n, p.lat);
          w = Math.min(w, p.lng); e = Math.max(e, p.lng);
        }
        return { s, n, w, e };
      }
      if (isFiniteBounds(item?.bounds)) {
        const b = item.bounds;
        return { s: b.south, n: b.north, w: b.west, e: b.east };
      }
      return null;
    };
    const inViewport = (item) => {
      const v = viewportRef.current;
      if (!v) return true;
      const t = tileBBox(item);
      if (!t) return true;
      return !(t.e < v.w || t.w > v.e || t.n < v.s || t.s > v.n);
    };

    const reportProgress = () => {
      let loaded = 0;
      for (const [, entry] of overlaysRef.current) {
        if (entry.overlay) loaded += 1;
      }
      const failed = erroredRef.current.size;
      // Count errored tiles as settled so the bar can reach total.
      if (onLoadProgressRef.current) onLoadProgressRef.current(loaded + failed, totalRef.current);
      if (onFailedChangeRef.current) onFailedChangeRef.current(failed);
    };

    const startLoad = (item) => {
      const name = item.name;
      inFlightRef.current.add(name);
      const priorAttempts = attemptsRef.current.get(name) || 0;
      // Cache-bust only on retry so a poisoned browser-cache entry is bypassed;
      // the first attempt keeps the clean URL so it can hit disk/CDN cache.
      const effectiveUrl =
        priorAttempts > 0
          ? `${item.url}${item.url.includes('?') ? '&' : '?'}r=${priorAttempts}`
          : item.url;
      const img = new Image();
      img.onload = () => {
        inFlightRef.current.delete(name);
        const slot = overlaysRef.current.get(name);
        // Slot removed (path deleted) or superseded by a newer url — drop.
        if (!slot || slot.url !== item.url) { pump(); return; }
        try {
          const overlay = isFiniteQuad(item?.quad)
            ? createImageQuadOverlayView(
                g,
                item.quad,
                effectiveUrl,
                overlayOpacityRef.current,
                { width: img.naturalWidth, height: img.naturalHeight },
                overlayBrightnessRef.current,
                overlayContrastRef.current
              )
            : createImageBoundsOverlayView(
                g,
                item.bounds,
                effectiveUrl,
                overlayOpacityRef.current,
                overlayBrightnessRef.current,
                overlayContrastRef.current
              );
          overlay.setMap(map);
          overlaysRef.current.set(name, { overlay, url: item.url, loading: false });
        } catch (_) {
          overlaysRef.current.delete(name);
        }
        reportProgress();
        pump();
      };
      img.onerror = () => {
        inFlightRef.current.delete(name);
        const slot = overlaysRef.current.get(name);
        if (!slot || slot.url !== item.url) { pump(); return; }
        const made = priorAttempts + 1;
        attemptsRef.current.set(name, made);
        if (made <= ORTHO_RETRY_DELAYS_MS.length) {
          const delay = ORTHO_RETRY_DELAYS_MS[made - 1] + Math.floor(Math.random() * 250);
          const t = setTimeout(() => {
            timersRef.current.delete(t);
            const cur = itemsByNameRef.current.get(name);
            const resv = overlaysRef.current.get(name);
            // Still wanted, same url, not already loaded → requeue.
            if (cur && cur.url === item.url && resv && !resv.overlay) {
              queueRef.current.push(cur);
              pump();
            }
          }, delay);
          timersRef.current.add(t);
        } else {
          // Exhausted retries — give up on this tile, surface it for retry.
          overlaysRef.current.delete(name);
          erroredRef.current.set(name, item.url);
          reportProgress();
        }
        pump();
      };
      img.crossOrigin = 'anonymous';
      img.src = effectiveUrl;
    };

    const pump = () => {
      if (!map) return;
      // Prune stale queue entries (unwanted, already loaded, or in flight).
      queueRef.current = queueRef.current.filter((it) => {
        const resv = overlaysRef.current.get(it.name);
        return (
          itemsByNameRef.current.has(it.name) &&
          !(resv && resv.overlay) &&
          !inFlightRef.current.has(it.name)
        );
      });
      while (inFlightRef.current.size < ORTHO_MAX_CONCURRENT && queueRef.current.length > 0) {
        // Viewport tiles first; otherwise FIFO.
        let idx = queueRef.current.findIndex((it) => inViewport(it));
        if (idx < 0) idx = 0;
        const item = queueRef.current.splice(idx, 1)[0];
        if (!item) break;
        startLoad(item);
      }
    };
    pumpRef.current = pump;

    // Honor a retry request: clear permanent-error markers so those tiles
    // re-enqueue below (their overlay reservation was already deleted).
    if (retryNonce !== lastRetryNonceRef.current) {
      lastRetryNonceRef.current = retryNonce;
      for (const name of Array.from(erroredRef.current.keys())) {
        erroredRef.current.delete(name);
        attemptsRef.current.delete(name);
      }
    }

    // Drop error markers for tiles no longer in the desired set.
    for (const name of Array.from(erroredRef.current.keys())) {
      if (!wantedNames.has(name)) erroredRef.current.delete(name);
    }

    // 1. Drop overlays whose tiles are no longer in the desired set
    //    (e.g. user deleted a path → its tiles vanish from imageOverlays).
    for (const [name, entry] of Array.from(overlaysRef.current.entries())) {
      if (!wantedNames.has(name)) {
        if (entry.overlay) {
          try { entry.overlay.setMap(null); } catch (_) { /* ignore */ }
        }
        overlaysRef.current.delete(name);
        attemptsRef.current.delete(name);
      }
    }

    // 2. Enqueue tiles not yet present. Existing entries with matching url are
    //    skipped (already loaded or queued/in-flight); a url change for the
    //    same name forces a re-enqueue with a fresh attempt counter.
    items.forEach((item) => {
      const existing = overlaysRef.current.get(item.name);
      if (existing && existing.url === item.url) return;
      if (existing && existing.overlay) {
        try { existing.overlay.setMap(null); } catch (_) { /* ignore */ }
      }
      attemptsRef.current.delete(item.name);
      erroredRef.current.delete(item.name);
      // Reserve the slot immediately so a concurrent reconcile sees this tile
      // is pending and doesn't kick off a duplicate load.
      overlaysRef.current.set(item.name, { overlay: null, url: item.url, loading: true });
      queueRef.current.push(item);
    });

    pump();
    // Report current progress so the loader bar reflects the desired total
    // (loaded count may be < total while loads are still in flight).
    reportProgress();

    // No cleanup on deps change — overlays are managed incrementally above.
    return undefined;
  }, [map, imageOverlays, retryNonce]);

  // Re-prioritise the queue when the viewport changes so newly-visible tiles
  // jump the line as load slots free up.
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const update = () => {
      const b = map.getBounds && map.getBounds();
      if (!b) return;
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      const s = sw.lat(); const n = ne.lat(); const w = sw.lng(); const e = ne.lng();
      // Pad by ~one screen on each side so tiles just off-screen preload.
      const dLat = (n - s) || 0.0001;
      const dLng = (e - w) || 0.0001;
      viewportRef.current = { s: s - dLat, n: n + dLat, w: w - dLng, e: e + dLng };
      if (pumpRef.current) pumpRef.current();
    };
    update();
    const listener = map.addListener('idle', update);
    return () => { if (listener) listener.remove(); };
  }, [map]);

  // Component unmount: wipe every overlay from the map and cancel retry timers.
  useEffect(() => {
    return () => {
      for (const t of timersRef.current) {
        try { clearTimeout(t); } catch (_) { /* ignore */ }
      }
      timersRef.current.clear();
      for (const [, entry] of overlaysRef.current) {
        if (entry.overlay) {
          try { entry.overlay.setMap(null); } catch (_) { /* ignore */ }
        }
      }
      overlaysRef.current.clear();
    };
  }, []);

  useEffect(() => {
    for (const [, entry] of overlaysRef.current) {
      if (entry.overlay && typeof entry.overlay.setOverlayOpacity === 'function') {
        entry.overlay.setOverlayOpacity(overlayOpacity);
      }
    }
  }, [overlayOpacity]);

  useEffect(() => {
    for (const [, entry] of overlaysRef.current) {
      if (entry.overlay && typeof entry.overlay.setOverlayFilter === 'function') {
        entry.overlay.setOverlayFilter(overlayBrightness, overlayContrast);
      }
    }
  }, [overlayBrightness, overlayContrast]);

  return null;
}

/**
 * Renders LAS/LAZ coverage outlines on the map (from selected overlay folder headers).
 * lasBoundingBoxes: Array<{ south, north, west, east, hullLatLng? }> in lat/lon.
 *
 * When `hullLatLng` (the convex hull of the file's actual points) is present we
 * draw that polygon as a SOLID outline — it is the true coverage. The dashed
 * rectangle is only the axis-aligned header extent, which overstates coverage
 * for diagonal/corridor scans; we keep it (faded) only when there is no hull,
 * so a path can't look "inside coverage" when it actually sits in an empty
 * corner of the bbox.
 */
function LasBoundingBoxOverlays({ lasBoundingBoxes = [], selectedIndex = -1 }) {
  const map = useGoogleMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !Array.isArray(lasBoundingBoxes) || lasBoundingBoxes.length === 0) {
      return () => {};
    }
    const g = window.google.maps;
    const overlays = [];
    lasBoundingBoxes.forEach((b, idx) => {
      if (!b || typeof b.south !== 'number' || typeof b.north !== 'number' || typeof b.west !== 'number' || typeof b.east !== 'number') {
        return;
      }
      const isSelected = idx === selectedIndex;
      const strokeColor = isSelected ? '#fc9b1c' : '#3b8cff';
      const strokeWeight = isSelected ? 3 : 2;

      const hull = Array.isArray(b.hullLatLng)
        ? b.hullLatLng.filter(
            (p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng),
          )
        : null;

      if (hull && hull.length >= 3) {
        // True footprint — solid polygon.
        const poly = new g.Polygon({
          paths: hull,
          strokeColor,
          strokeOpacity: 1,
          strokeWeight,
          fillColor: strokeColor,
          fillOpacity: isSelected ? 0.1 : 0.06,
          clickable: false,
          geodesic: false,
          zIndex: isSelected ? 12 : 10,
        });
        poly.setMap(map);
        overlays.push(poly);
        return;
      }

      // No hull yet — dashed header-extent rectangle (approximate coverage).
      const dashSymbol = {
        path: 'M 0,-1 0,1',
        strokeOpacity: 1,
        strokeColor,
        scale: strokeWeight,
      };
      const path = [
        { lat: b.south, lng: b.west },
        { lat: b.north, lng: b.west },
        { lat: b.north, lng: b.east },
        { lat: b.south, lng: b.east },
        { lat: b.south, lng: b.west },
      ];
      const line = new g.Polyline({
        path,
        strokeOpacity: 0,
        strokeWeight,
        clickable: false,
        geodesic: false,
        icons: [{
          icon: dashSymbol,
          offset: '0',
          repeat: isSelected ? '12px' : '10px',
        }],
        zIndex: isSelected ? 12 : 10,
      });
      line.setMap(map);
      overlays.push(line);
    });
    return () => {
      overlays.forEach((o) => o.setMap(null));
    };
  }, [map, lasBoundingBoxes, selectedIndex]);
  return null;
}

/** Triggers map resize when tab becomes visible (fixes layout after tab switch) */
function MapResizer({ isVisible }) {
  const map = useGoogleMap();
  useEffect(() => {
    LOG('MapResizer', 'effect', { hasMap: !!map, isVisible });
    if (!map || !isVisible || !window.google?.maps?.event) return;
    const t = setTimeout(() => {
      LOG('MapResizer', 'triggering resize');
      window.google.maps.event.trigger(map, 'resize');
    }, 100);
    return () => clearTimeout(t);
  }, [map, isVisible]);
  return null;
}

export default PathSelectorMap;
